import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { isErpIntegration } from '../utils/integrationRoles';

const JournalEntries = () => {
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLinesModal, setShowLinesModal] = useState(false);
  const [showAccountingExportModal, setShowAccountingExportModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [qboIntegrations, setQboIntegrations] = useState([]);
  const [selectedQBOIntegration, setSelectedQBOIntegration] = useState(null);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [selectedIntegrationForEntry, setSelectedIntegrationForEntry] = useState(null);
  const [allIntegrations, setAllIntegrations] = useState([]);
  const [qboConnectionStatus, setQboConnectionStatus] = useState('disconnected');
  const [qboCompanyInfo, setQboCompanyInfo] = useState(null);
  const [qboConnectionLoading, setQboConnectionLoading] = useState(false);
  const [qboMockMode, setQboMockMode] = useState(false);
  const [exportStep, setExportStep] = useState(1); // 1: Select Accounting Period, 2: Select Projects, 3: Select Data Types, 4: Export
  const [csvExportLoading, setCsvExportLoading] = useState(false);
  const [selectedAccountingPeriod, setSelectedAccountingPeriod] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedDataTypes, setSelectedDataTypes] = useState({
    journals: false,
    ap_invoices: false,
    project_billings: false
  });
  const [showExportResultsModal, setShowExportResultsModal] = useState(false);
  const [exportResults, setExportResults] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [sendingToQBO, setSendingToQBO] = useState({}); // Track which entries are being sent
  const [qboJournalEntryIds, setQboJournalEntryIds] = useState({}); // Track QBO IDs for each entry (retainage journal entries)
  const [qboBillIds, setQboBillIds] = useState({}); // Track QBO Bill IDs for each entry (maps journal entry VUID to Bill ID)
  const [qboInvoiceIds, setQboInvoiceIds] = useState({}); // Track QBO Invoice IDs for each entry (maps journal entry VUID to Invoice ID for project billings)
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [entryMappings, setEntryMappings] = useState([]); // All mappings for the selected entry
  const [mappingLoading, setMappingLoading] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null); // Which mapping is being edited
  const [editedMappingId, setEditedMappingId] = useState(''); // Edited external ID value
  const [integrationMetadata, setIntegrationMetadata] = useState({}); // Store realm_id per integration for URL construction
  const [filters, setFilters] = useState({
    project_vuid: '',
    accounting_period_vuid: '',
    reference_type: ''
  });
  const [syncStatus, setSyncStatus] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    let data = journalEntries;
    if (syncStatus === 'synced') {
      data = data.filter(entry =>
        entry.is_synced || qboBillIds[entry.vuid] || qboInvoiceIds[entry.vuid] || qboJournalEntryIds[entry.vuid]
      );
    } else if (syncStatus === 'unsynced') {
      data = data.filter(entry =>
        !entry.is_synced && !qboBillIds[entry.vuid] && !qboInvoiceIds[entry.vuid] && !qboJournalEntryIds[entry.vuid]
      );
    }

    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle nested properties
      if (sortConfig.key === 'project') {
        aValue = a.project?.project_name || '';
        bValue = b.project?.project_name || '';
      } else if (sortConfig.key === 'project_number') {
        aValue = a.project?.project_number || '';
        bValue = b.project?.project_number || '';
      } else if (sortConfig.key === 'entry_date') {
        aValue = a.entry_date ? new Date(a.entry_date).getTime() : 0;
        bValue = b.entry_date ? new Date(b.entry_date).getTime() : 0;
      } else if (sortConfig.key === 'amount') {
        // Calculate amount from line items
        const aLineItemsTotal = a.line_items?.reduce((sum, line) => sum + parseFloat(line.debit_amount || 0), 0) || 0;
        const bLineItemsTotal = b.line_items?.reduce((sum, line) => sum + parseFloat(line.debit_amount || 0), 0) || 0;
        aValue = aLineItemsTotal > 0 ? aLineItemsTotal : (a.total_amount || 0);
        bValue = bLineItemsTotal > 0 ? bLineItemsTotal : (b.total_amount || 0);
      }

      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Handle numeric values
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      }

      return 0;
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    if (sortConfig.direction === 'asc') {
      return (
        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    
    return (
      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };
  
  // Form data for create/edit
  const [formData, setFormData] = useState({
    accounting_period_vuid: '',
    project_vuid: '',
    entry_date: new Date().toISOString().split('T')[0],
    description: '',
    reference_type: '',
    reference_vuid: ''
  });
  
  // Line items for create/edit
  const [lineItems, setLineItems] = useState([
    { line_number: 1, gl_account_vuid: '', description: '', debit_amount: 0, credit_amount: 0 }
  ]);
  
  // Reference data
  const [projects, setProjects] = useState([]);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  
  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  const location = useLocation();
  const navigate = useNavigate();

  // Get context from URL parameters if available
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const projectVuid = searchParams.get('project_vuid');
    const accountingPeriodVuid = searchParams.get('accounting_period_vuid');
    
    if (projectVuid) {
      setFilters(prev => ({ ...prev, project_vuid: projectVuid }));
      setFormData(prev => ({ ...prev, project_vuid: projectVuid }));
    }
    
    if (accountingPeriodVuid) {
      setFilters(prev => ({ ...prev, accounting_period_vuid: accountingPeriodVuid }));
    }
  }, [location]);

  // Fetch reference data
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const [projectsRes, periodsRes, openPeriodsRes, accountsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/projects`),
          axios.get(`${API_BASE_URL}/api/accounting-periods`),
          axios.get(`${API_BASE_URL}/api/accounting-periods/open`),
          axios.get(`${API_BASE_URL}/api/chartofaccounts`)
        ]);
        
        setProjects(projectsRes.data || []);
        setAccountingPeriods(periodsRes.data || []);
        setGlAccounts(accountsRes.data || []);
        
        // Auto-select accounting period if only one open period exists
        const openPeriods = openPeriodsRes.data || [];
        if (openPeriods.length === 1) {
          // Check if period is already set from URL params
          const searchParams = new URLSearchParams(location.search);
          const accountingPeriodVuid = searchParams.get('accounting_period_vuid');
          if (!accountingPeriodVuid) {
            setFilters(prev => ({ ...prev, accounting_period_vuid: openPeriods[0].vuid }));
          }
        }
      } catch (error) {
        console.error('Error fetching reference data:', error);
        // Set empty arrays on error to prevent undefined issues
        setProjects([]);
        setAccountingPeriods([]);
        setGlAccounts([]);
      }
    };
    
    fetchReferenceData();
  }, []);

  /** Accounting export Step 2: all projects when page filter is All Projects; otherwise the single filtered project. */
  const getExportProjectSelectionFromFilters = () => {
    const list = projects || [];
    if (!list.length) return [];
    const pv = (filters.project_vuid || '').trim();
    if (!pv) {
      return list.map((p) => p.vuid);
    }
    return list.some((p) => p.vuid === pv) ? [pv] : [];
  };

  // Fetch integrations that support sending journal entries
  useEffect(() => {
    fetchJournalEntryIntegrations();
  }, []);

  // Fetch journal entries - only when accounting period is selected
  useEffect(() => {
    setError(null); // Clear errors when filters change
    // Only fetch if accounting period is selected
    if (filters.accounting_period_vuid) {
      fetchJournalEntries();
    } else {
      // Clear entries if no period selected
      setJournalEntries([]);
      setLoading(false);
    }
    // Don't auto-test connection - let user initiate it manually
  }, [filters, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [syncStatus]);
  
  // Note: QBO journal entry IDs are fetched when needed (when viewing entries)

  const fetchJournalEntries = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const response = await axios.get(`${API_BASE_URL}/api/journal-entries?${queryParams}`);
      const entries = response.data;
      setJournalEntries(entries);
      // Note: totalPages will be calculated dynamically from sorted data
      
      // Fetch QBO journal entry IDs for all entries (fetch for all QBO integrations, not just selected one)
      if (entries.length > 0) {
        // Get all QBO integrations to fetch IDs
        const allQboIntegrations = qboIntegrations.length > 0 ? qboIntegrations : 
          (await axios.get(`${API_BASE_URL}/api/integrations`)).data.filter(
            i => isErpIntegration(i) && i.status === 'active'
          );
        
        if (allQboIntegrations.length > 0) {
          // Fetch IDs for the first QBO integration (or we could fetch for all)
          const integrationToUse = selectedQBOIntegration || allQboIntegrations[0];
          if (integrationToUse) {
            await fetchQBOJournalEntryIds(entries.map(e => e.vuid), integrationToUse.vuid, entries);
          }
        }
      }
      
      setError(null); // Clear error on success
    } catch (error) {
      setError(error.response?.data?.error || 'Error fetching journal entries');
      console.error('Error:', error);
      setJournalEntries([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };
  
  const fetchQBOJournalEntryIds = async (journalEntryVuids, integrationVuid = null, entries = null) => {
    if (journalEntryVuids.length === 0) return;
    
    const integrationToUse = integrationVuid || selectedQBOIntegration?.vuid;
    if (!integrationToUse) return;
    
    // Use provided entries or fall back to state
    const entriesToUse = entries || journalEntries;
    
    try {
      // Fetch journal entry IDs (for retainage entries)
      const jeResponse = await axios.get(`${API_BASE_URL}/api/external-system-ids`, {
        params: {
          integration_vuid: integrationToUse,
          object_type: 'journal_entry'
        }
      });
      
      const jeMappings = jeResponse.data || [];
      const qboIdsMap = {};
      jeMappings.forEach(mapping => {
        if (mapping.object_vuid && journalEntryVuids.includes(mapping.object_vuid)) {
          qboIdsMap[mapping.object_vuid] = mapping.external_id;
        }
      });
      setQboJournalEntryIds(prev => ({ ...prev, ...qboIdsMap }));
      
      // Fetch Bill IDs (for AP invoices)
      // First, get all AP invoice VUIDs from journal entries
      const apInvoiceVuids = entriesToUse
        .filter(entry => entry.transaction_type === 'ap_invoice' && entry.transaction_vuid)
        .map(entry => entry.transaction_vuid);
      
      if (apInvoiceVuids.length > 0) {
        const billResponse = await axios.get(`${API_BASE_URL}/api/external-system-ids`, {
          params: {
            integration_vuid: integrationToUse,
            object_type: 'ap_invoice'
          }
        });
        
        const billMappings = billResponse.data || [];
        const billIdsMap = {};
        // Create a map from journal entry VUID to Bill ID
        // IMPORTANT: Only map to the bill record, not retainage entries
        // Retainage entries have reference_type === 'Retainage', bill records have other reference_type values
        billMappings.forEach(mapping => {
          if (mapping.object_vuid && apInvoiceVuids.includes(mapping.object_vuid)) {
            // Find the bill journal entry that corresponds to this AP invoice
            // Retainage entries have reference_type === 'Retainage' and should NOT get the bill external ID
            const journalEntry = entriesToUse.find(
              entry => entry.transaction_vuid === mapping.object_vuid 
                && entry.transaction_type === 'ap_invoice'
                && entry.reference_type !== 'Retainage'  // Only bill records, not retainage entries
            );
            if (journalEntry) {
              billIdsMap[journalEntry.vuid] = mapping.external_id;
            }
          }
        });
        // Only update IDs for bill entries, not retainage entries
        const relevantVuids = entriesToUse
          .filter(entry => entry.transaction_type === 'ap_invoice' 
            && entry.transaction_vuid 
            && entry.reference_type !== 'Retainage')  // Only bill records, not retainage entries
          .map(entry => entry.vuid);
        setQboBillIds(prev => {
          // Keep only mappings for entries in the current list, then merge with new mappings
          const filtered = Object.fromEntries(
            Object.entries(prev).filter(([vuid]) => relevantVuids.includes(vuid))
          );
          return { ...filtered, ...billIdsMap };
        });
      }
      
      // Fetch Invoice IDs (for project billings)
      const projectBillingVuids = entriesToUse
        .filter(entry => entry.transaction_type === 'project_billing' && entry.transaction_vuid)
        .map(entry => entry.transaction_vuid);
      
      if (projectBillingVuids.length > 0) {
        const invoiceResponse = await axios.get(`${API_BASE_URL}/api/external-system-ids`, {
          params: {
            integration_vuid: integrationToUse,
            object_type: 'project_billing'
          }
        });
        
        const invoiceMappings = invoiceResponse.data || [];
        const invoiceIdsMap = {};
        // Create a map from journal entry VUID to Invoice ID
        // IMPORTANT: Only map to the invoice record, not retainage entries
        // Retainage entries have reference_type === 'Retainage', invoice records have other reference_type values
        invoiceMappings.forEach(mapping => {
          if (mapping.object_vuid && projectBillingVuids.includes(mapping.object_vuid)) {
            // Find the invoice journal entry that corresponds to this project billing
            // Retainage entries have reference_type === 'Retainage' and should NOT get the invoice external ID
            const journalEntry = entriesToUse.find(
              entry => entry.transaction_vuid === mapping.object_vuid 
                && entry.transaction_type === 'project_billing'
                && entry.reference_type !== 'Retainage'  // Only invoice records, not retainage entries
            );
            if (journalEntry) {
              invoiceIdsMap[journalEntry.vuid] = mapping.external_id;
            }
          }
        });
        // Only update IDs for invoice entries, not retainage entries
        const relevantVuids = entriesToUse
          .filter(entry => entry.transaction_type === 'project_billing' 
            && entry.transaction_vuid 
            && entry.reference_type !== 'Retainage')  // Only invoice records, not retainage entries
          .map(entry => entry.vuid);
        setQboInvoiceIds(prev => {
          // Keep only mappings for entries in the current list, then merge with new mappings
          const filtered = Object.fromEntries(
            Object.entries(prev).filter(([vuid]) => relevantVuids.includes(vuid))
          );
          return { ...filtered, ...invoiceIdsMap };
        });
      }
    } catch (error) {
      console.error('Error fetching QBO IDs:', error);
    }
  };
  
  // Helper function to construct QBO URL from external_id, external_object_type, and integration environment
  const constructQboUrl = (externalId, externalObjectType, integrationEnv) => {
    if (!externalId) return null;
    
    const typeMap = {
      'journal_entry': 'journal',  // Journal entries use 'journal' not 'journalentry'
      'bill': 'bill',
      'invoice': 'invoice'
    };
    
    const qboType = typeMap[externalObjectType] || 'journal';
    
    // Determine base URL based on environment
    const baseUrl = (integrationEnv === 'sandbox') 
      ? 'https://sandbox.qbo.intuit.com'
      : 'https://app.qbo.intuit.com';
    
    return `${baseUrl}/app/${qboType}?txnId=${externalId}`;
  };
  
  // Fetch all mappings for a journal entry
  const fetchEntryMappings = async (entry) => {
    setMappingLoading(true);
    try {
      // Get all integrations first
      const integrationsResponse = await axios.get(`${API_BASE_URL}/api/integrations`);
      const integrations = integrationsResponse.data.filter(i => i.status === 'active');
      
      // Store environment and realm_id from each integration's custom_metadata for URL construction
      const metadataMap = {};
      integrations.forEach(integration => {
        if (integration.custom_metadata) {
          metadataMap[integration.vuid] = {
            realm_id: integration.custom_metadata.realm_id,
            environment: integration.custom_metadata.environment || 'production'
          };
        }
      });
      setIntegrationMetadata(metadataMap);
      
      const allMappings = [];
      
      // Determine which object types to fetch based on the entry's transaction type
      const objectTypes = [];
      
      // Always check for journal_entry mapping (using entry.vuid)
      objectTypes.push({ type: 'journal_entry', vuid: entry.vuid });
      
      // If this is an AP invoice entry, only check for ap_invoice mapping if it's the bill record (not retainage)
      // Retainage entries have reference_type === 'Retainage' and should only show journal_entry mappings, not bill mappings
      if (entry.transaction_type === 'ap_invoice' && entry.transaction_vuid) {
        // Only check for ap_invoice (bill) mapping if this is the bill record, not a retainage entry
        if (entry.reference_type !== 'Retainage') {
          objectTypes.push({ type: 'ap_invoice', vuid: entry.transaction_vuid });
        }
      }
      
      // If this is a project billing entry, only check for project_billing mapping if it's the invoice record (not retainage)
      // Retainage entries have reference_type === 'Retainage' and should only show journal_entry mappings, not invoice mappings
      if (entry.transaction_type === 'project_billing' && entry.transaction_vuid) {
        // Only check for project_billing (invoice) mapping if this is the invoice record, not a retainage entry
        if (entry.reference_type !== 'Retainage') {
          objectTypes.push({ type: 'project_billing', vuid: entry.transaction_vuid });
        }
      }
      
      // Fetch mappings for each integration and object type combination
      for (const integration of integrations) {
        for (const { type, vuid } of objectTypes) {
          try {
            const mappingResponse = await axios.get(`${API_BASE_URL}/api/external-system-ids`, {
              params: {
                integration_vuid: integration.vuid,
                object_type: type,
                object_vuid: vuid  // Use the specific VUID for this object type
              }
            });
            
            if (mappingResponse.data && mappingResponse.data.length > 0) {
              // Filter to ensure we only get mappings that match this specific entry
              mappingResponse.data.forEach(mapping => {
                // Double-check that the object_vuid matches
                if (mapping.object_vuid === vuid) {
                  // Only construct QBO URL for QBO integrations
                  let qboUrl = mapping.external_metadata?.qbo_url;
                  const integrationType = (integration.integration_type || '').toLowerCase();
                  const isQboIntegration = integrationType.includes('quickbooks') || integrationType.includes('qbo');
                  
                  if (!qboUrl && isQboIntegration && mapping.external_id && mapping.external_object_type) {
                    // Get environment from stored metadata or integration
                    const integrationInfo = metadataMap[integration.vuid] || {};
                    const integrationEnv = integrationInfo.environment || integration.custom_metadata?.environment || 'production';
                    qboUrl = constructQboUrl(
                      mapping.external_id,
                      mapping.external_object_type,
                      integrationEnv
                    );
                  }
                  
                  // Merge URL into metadata for display
                  const metadata = {
                    ...(mapping.external_metadata || {}),
                    ...(qboUrl ? { qbo_url: qboUrl } : {})
                  };
                  
                  allMappings.push({
                    ...mapping,
                    external_metadata: metadata,
                    integration_name: integration.integration_name,
                    integration_type: integration.integration_type
                  });
                }
              });
            }
          } catch (e) {
            // Continue checking other integrations/types
            console.warn(`Error fetching mapping for ${type} with vuid ${vuid}:`, e);
          }
        }
      }
      
      setEntryMappings(allMappings);
    } catch (error) {
      console.error('Error fetching entry mappings:', error);
      alert('Error fetching mappings: ' + (error.response?.data?.error || error.message));
    } finally {
      setMappingLoading(false);
    }
  };
  
  // Handle showing mapping modal
  const handleShowMapping = async (entry) => {
    setSelectedEntry(entry);
    setShowMappingModal(true);
    setEntryMappings([]);
    setEditingMapping(null);
    await fetchEntryMappings(entry);
  };
  
  // Handle editing a mapping
  const handleEditMapping = (mapping) => {
    setEditingMapping(mapping.vuid);
    setEditedMappingId(mapping.external_id);
  };
  
  // Handle saving edited mapping
  const handleSaveMapping = async (mapping) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/external-system-ids/${mapping.vuid}`, {
        external_id: editedMappingId
      });
      
      if (response.data) {
        alert('✅ Mapping updated successfully!');
        setEditingMapping(null);
        // Refresh mappings
        if (selectedEntry) {
          await fetchEntryMappings(selectedEntry);
          // Also refresh the main IDs in state
          await fetchQBOJournalEntryIds([selectedEntry.vuid]);
        }
      }
    } catch (error) {
      alert('❌ Error updating mapping: ' + (error.response?.data?.error || error.message));
    }
  };
  
  // Handle clearing a mapping from modal
  const handleClearMappingFromModal = async (mapping, entry) => {
    if (!window.confirm(`Clear the mapping for this ${mapping.object_type} (${mapping.external_id})? This will allow you to re-send it.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/external-system-ids/by-object`,
        {
          params: {
            integration_vuid: mapping.integration_vuid,
            object_type: mapping.object_type,
            object_vuid: mapping.object_vuid
          }
        }
      );
      
      if (response.data.deleted_count > 0) {
        alert(`✅ Successfully cleared mapping.`);
        
        // Remove from state
        if (mapping.object_type === 'project_billing') {
          setQboInvoiceIds(prev => {
            const newState = { ...prev };
            delete newState[entry.vuid];
            return newState;
          });
        } else if (mapping.object_type === 'ap_invoice') {
          setQboBillIds(prev => {
            const newState = { ...prev };
            delete newState[entry.vuid];
            return newState;
          });
        } else if (mapping.object_type === 'journal_entry') {
          setQboJournalEntryIds(prev => {
            const newState = { ...prev };
            delete newState[entry.vuid];
            return newState;
          });
        }
        
        // Refresh mappings in modal
        await fetchEntryMappings(entry);
        // Refresh main IDs
        await fetchQBOJournalEntryIds([entry.vuid]);
        // Refresh journal entries to update is_synced status
        await fetchJournalEntries();
      } else {
        alert('No mappings found to clear.');
      }
    } catch (error) {
      alert('❌ Error clearing mapping: ' + (error.response?.data?.error || error.message));
    }
  };
  
  const handleClearMapping = async (entry, objectType = 'journal_entry', integration = null) => {
    // Try to find which integration has the mapping
    let integrationToUse = integration;
    
    if (!integrationToUse) {
      // Fetch all integrations and find which one has a mapping for this entry
      try {
        const response = await axios.get(`${API_BASE_URL}/api/integrations`);
        const integrations = response.data.filter(i => i.status === 'active');
        
        // Check each integration for a mapping
        // For project_billing and ap_invoice, use transaction_vuid instead of entry.vuid
        const lookupVuid = (objectType === 'project_billing' || objectType === 'ap_invoice') && entry.transaction_vuid
          ? entry.transaction_vuid
          : entry.vuid;
        
        for (const int of integrations) {
          try {
            const mappingResponse = await axios.get(`${API_BASE_URL}/api/external-system-ids`, {
              params: {
                integration_vuid: int.vuid,
                object_type: objectType,
                object_vuid: lookupVuid
              }
            });
            if (mappingResponse.data && mappingResponse.data.length > 0) {
              integrationToUse = int;
              break;
            }
          } catch (e) {
            // Continue checking other integrations
          }
        }
      } catch (error) {
        console.error('Error finding integration with mapping:', error);
      }
    }
    
    // Fallback to first QBO integration if still not found
    if (!integrationToUse) {
      integrationToUse = selectedQBOIntegration || (qboIntegrations.length > 0 ? qboIntegrations[0] : null);
    }
    
    if (!integrationToUse) {
      alert('No integration found. Please select an integration first.');
      return;
    }
    
    if (!window.confirm(`Clear the mapping for this ${objectType}? This will allow you to re-send it to ${integrationToUse.integration_name}.`)) {
      return;
    }
    
    try {
      // For project_billing and ap_invoice, use transaction_vuid instead of entry.vuid
      // because the mapping is stored with the transaction VUID, not the journal entry VUID
      const objectVuid = (objectType === 'project_billing' || objectType === 'ap_invoice') && entry.transaction_vuid
        ? entry.transaction_vuid
        : entry.vuid;
      
      const response = await axios.delete(
        `${API_BASE_URL}/api/external-system-ids/by-object`,
        {
          params: {
            integration_vuid: integrationToUse.vuid,
            object_type: objectType,
            object_vuid: objectVuid
          }
        }
      );
      
      if (response.data.deleted_count > 0) {
        alert(`✅ Successfully cleared ${response.data.deleted_count} mapping(s). You can now re-send this to ${integrationToUse.integration_name}.`);
        
        // Immediately remove from state based on object type (before refresh)
        if (objectType === 'project_billing') {
          setQboInvoiceIds(prev => {
            const newState = { ...prev };
            delete newState[entry.vuid];
            return newState;
          });
        } else if (objectType === 'ap_invoice') {
          setQboBillIds(prev => {
            const newState = { ...prev };
            delete newState[entry.vuid];
            return newState;
          });
        } else if (objectType === 'journal_entry') {
          setQboJournalEntryIds(prev => {
            const newState = { ...prev };
            delete newState[entry.vuid];
            return newState;
          });
        }
        
        // Refresh journal entries (which will also refresh QBO IDs via useEffect)
        // This is important to update the is_synced status from the API
        await fetchJournalEntries();
      } else {
        alert('No mappings found to clear.');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || 'Failed to clear mapping';
      alert(`❌ Error: ${errorMsg}`);
      console.error('Error clearing mapping:', error);
    }
  };

  const handleSendToIntegration = async (entry, integration, forceResend = false) => {
    if (!integration) {
      alert('No integration selected. Please select an integration first.');
      return;
    }
    
    // Confirmation is handled in the modal before calling this function
    // No need to ask again here
    
    try {
      setSendingToQBO(prev => ({ ...prev, [entry.vuid]: true }));
      
      const response = await axios.post(
        `${API_BASE_URL}/api/integrations/${integration.vuid}/send-journal-entry`,
        { 
          journal_entry_vuid: entry.vuid,
          force_resend: forceResend
        }
      );
      
        if (response.data.success) {
        // Handle both regular journal entries and combined entries (Bill + Retainage for AP, Invoice + Retainage for Project Billing)
        let qboId, journalNumber, message;
        
        if (response.data.retainage_journal_entry || response.data.bill || response.data.invoice) {
          // Combined entry - has either Bill + Retainage (AP Invoice) or Invoice + Retainage (Project Billing)
          const bill = response.data.bill;
          const invoice = response.data.invoice;
          const retainage = response.data.retainage_journal_entry;
          
          qboId = retainage?.qbo_journal_entry_id;
          journalNumber = retainage?.journal_number;
          
          let billInfo = '';
          if (bill) {
            if (bill.qbo_bill_id && !bill.already_sent) {
              billInfo = `\n\n📄 Bill Created:\n- QBO Bill ID: ${bill.qbo_bill_id}\n- Invoice Number: ${bill.invoice_number || 'N/A'}`;
            } else if (bill.already_sent) {
              billInfo = `\n\n📄 Bill: Already sent (QBO Bill ID: ${bill.qbo_bill_id})`;
            } else if (bill.qbo_bill_id) {
              billInfo = `\n\n📄 Bill:\n- QBO Bill ID: ${bill.qbo_bill_id}`;
            }
          }
          
          let invoiceInfo = '';
          if (invoice) {
            if (invoice.qbo_invoice_id && !invoice.already_sent) {
              invoiceInfo = `\n\n📄 Invoice Created:\n- QBO Invoice ID: ${invoice.qbo_invoice_id}\n- Billing Number: ${invoice.billing_number || 'N/A'}`;
            } else if (invoice.already_sent) {
              invoiceInfo = `\n\n📄 Invoice: Already sent (QBO Invoice ID: ${invoice.qbo_invoice_id})`;
            } else if (invoice.qbo_invoice_id) {
              invoiceInfo = `\n\n📄 Invoice:\n- QBO Invoice ID: ${invoice.qbo_invoice_id}`;
            }
          }
          
          if (response.data.bill_error) {
            billInfo += `\n⚠️ Bill Error: ${response.data.bill_error}`;
          }
          if (response.data.invoice_error) {
            invoiceInfo += `\n⚠️ Invoice Error: ${response.data.invoice_error}`;
          }
          if (response.data.retainage_error) {
            billInfo += `\n⚠️ Retainage Error: ${response.data.retainage_error}`;
            invoiceInfo += `\n⚠️ Retainage Error: ${response.data.retainage_error}`;
          }
          
          let retainageInfo = '';
          if (retainage && retainage.qbo_journal_entry_id) {
            retainageInfo = `\n\n📝 Retainage Journal Entry:\n- QBO Journal Entry ID: ${retainage.qbo_journal_entry_id}\n- Journal Number: ${retainage.journal_number || 'N/A'}`;
            if (retainage.already_sent) {
              retainageInfo += ' (Already sent)';
            }
          } else if (response.data.retainage_error) {
            retainageInfo = `\n\n📝 Retainage Journal Entry: Failed to create\n⚠️ Error: ${response.data.retainage_error}`;
          } else {
            retainageInfo = `\n\n📝 Retainage Journal Entry: Not created (No error message provided)`;
          }
          
          message = `✅ Successfully sent to QuickBooks Online!${retainageInfo}${billInfo}${invoiceInfo}\n\nYou can search for these in QBO using:${qboId ? `\n- Journal Entry ID: ${qboId}` : ''}${journalNumber ? `\n- Doc Number: ${journalNumber}` : ''}`;
        } else {
          // Regular journal entry
          qboId = response.data.qbo_journal_entry_id;
          journalNumber = response.data.journal_number;
          
          message = `✅ Successfully sent to QuickBooks Online!\n\nQBO Journal Entry ID: ${qboId || 'N/A'}\nJournal Number: ${journalNumber || 'N/A'}\n\nYou can search for this in QBO using:\n- Journal Entry ID: ${qboId || 'N/A'}\n- Doc Number: ${journalNumber || 'N/A'}`;
        }

        if (response.data.procore_direct_cost && response.data.procore_direct_cost.procore_direct_cost_id) {
          const dc = response.data.procore_direct_cost;
          message += `\n\n📤 Procore Direct Cost: ${dc.already_sent ? 'Already sent' : 'Created'} (ID ${dc.procore_direct_cost_id}${dc.direct_cost_type ? `, type ${dc.direct_cost_type}` : ''})`;
        } else if (response.data.procore_direct_cost_error) {
          message += `\n\n⚠️ Procore Direct Cost failed:\n${response.data.procore_direct_cost_error}`;
        }
        
        // Update the QBO journal entry ID if we have one
        if (qboId) {
          setQboJournalEntryIds(prev => ({
            ...prev,
            [entry.vuid]: qboId
          }));
        }
        
        // Update the Bill ID if we have one
        // IMPORTANT: Only set bill ID for bill records, not retainage entries
        if (response.data.bill && response.data.bill.qbo_bill_id) {
          // Only set bill ID if this is the bill record, not a retainage entry
          // Retainage entries have reference_type === 'Retainage'
          if (entry.reference_type !== 'Retainage') {
            setQboBillIds(prev => ({
              ...prev,
              [entry.vuid]: response.data.bill.qbo_bill_id
            }));
          }
        }
        
        // Update the Invoice ID if we have one
        // IMPORTANT: Only set invoice ID for invoice records, not retainage entries
        if (response.data.invoice && response.data.invoice.qbo_invoice_id) {
          // Only set invoice ID if this is the invoice record, not a retainage entry
          // Retainage entries have reference_type === 'Retainage'
          if (entry.reference_type !== 'Retainage') {
            setQboInvoiceIds(prev => ({
              ...prev,
              [entry.vuid]: response.data.invoice.qbo_invoice_id
            }));
          }
        }
        
        // Refresh the journal entries to show the QBO ID badges
        // Also refresh QBO IDs in case mappings were created
        await fetchQBOJournalEntryIds([entry.vuid], integration.vuid, journalEntries);
        fetchJournalEntries();
        
        alert(message);
      } else {
        throw new Error(response.data.error || 'Failed to send journal entry');
      }
    } catch (error) {
      // Extract detailed error message from response
      let errorMsg = 'Failed to send journal entry to QuickBooks Online';
      
      if (error.response?.data) {
        // Try to get the main error message
        errorMsg = error.response.data.error || errorMsg;
        
        // If journal entry not found (404), refresh the list and show helpful message
        if (error.response.status === 404 && errorMsg.toLowerCase().includes('not found')) {
          errorMsg = 'Journal entry not found. The entry may have been deleted. Refreshing the list...';
          fetchJournalEntries(); // Refresh the list
        }
        
        // Add QBO-specific error details if available
        if (error.response.data.qbo_error_code || error.response.data.qbo_error_detail) {
          let details = [];
          if (error.response.data.qbo_error_code) {
            details.push(`Code: ${error.response.data.qbo_error_code}`);
          }
          if (error.response.data.qbo_error_detail) {
            details.push(`Detail: ${error.response.data.qbo_error_detail}`);
          }
          if (details.length > 0) {
            errorMsg += `\n\n${details.join('\n')}`;
          }
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      alert(`❌ Error: ${errorMsg}`);
      console.error('Error sending journal entry to QBO:', error);
      console.error('Full error response:', error.response?.data);
    } finally {
      setSendingToQBO(prev => {
        const updated = { ...prev };
        delete updated[entry.vuid];
        return updated;
      });
    }
  };

  const handleCreateEntry = async () => {
    try {
      // Validate line items
      const totalDebits = lineItems.reduce((sum, line) => sum + parseFloat(line.debit_amount || 0), 0);
      const totalCredits = lineItems.reduce((sum, line) => sum + parseFloat(line.credit_amount || 0), 0);
      
      if (Math.abs(totalDebits - totalCredits) > 0.01) {
        setError('Total debits must equal total credits');
        return;
      }
      
      // Create journal entry
      const entryResponse = await fetch(`${baseURL}/api/journal-entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!entryResponse.ok) {
        throw new Error('Failed to create journal entry');
      }
      
      const entry = await entryResponse.json();
      
      // Create line items
      for (const line of lineItems) {
        if (line.gl_account_vuid && (line.debit_amount > 0 || line.credit_amount > 0)) {
          await fetch(`${baseURL}/api/journal-entries/${entry.vuid}/line-items`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(line)
          });
        }
      }
      
      setShowCreateModal(false);
      setFormData({
        accounting_period_vuid: '',
        project_vuid: '',
        entry_date: new Date().toISOString().split('T')[0],
        description: '',
        reference_type: '',
        reference_vuid: '',
        status: 'draft'
      });
      setLineItems([{ line_number: 1, gl_account_vuid: '', description: '', debit_amount: 0, credit_amount: 0 }]);
      fetchJournalEntries();
      
    } catch (error) {
      setError(`Error creating journal entry: ${error.message}`);
    }
  };


  // Old QBO export functions removed - use "Send to Accounting" modal instead

  // Fetch integrations that support sending journal entries
  const fetchJournalEntryIntegrations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/integrations`);
      // Filter integrations that support sending journal entries
      const journalEntryIntegrations = response.data.filter(
        integration => {
          // Check if integration has enabled_objects with journal_entries send capability
          const enabledObjects = integration.enabled_objects || {};
          const supportsJournalEntries = 
            isErpIntegration(integration) ||
            enabledObjects.journal_entries?.includes('send') ||
            enabledObjects.journals?.includes('send');
          
          return integration.status === 'active' && supportsJournalEntries;
        }
      );
      setQboIntegrations(journalEntryIntegrations);
    } catch (error) {
      console.error('Error fetching journal entry integrations:', error);
    }
  };

  // QuickBooks Online Connection Functions
  const testQBOConnection = async () => {
    if (!selectedQBOIntegration) {
      alert('Please select a QuickBooks Online integration first');
      return;
    }

    try {
      setQboConnectionLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/qbo/test-connection`, {
        params: { integration_vuid: selectedQBOIntegration.vuid }
      });
      
      if (response.data.success) {
        const mockMode = response.data.mock_mode || false;
        setQboMockMode(mockMode);
        setQboConnectionStatus('connected');
        setQboCompanyInfo(response.data.company_name);
        
        // Only show alert for mock mode to inform user
        if (mockMode) {
          alert(`✅ Connection Test Successful (Mock Mode)\n\nConnected to: ${response.data.company_name || 'QuickBooks Online'}\n\nNote: This is a mock connection. Configure your QBO credentials in the Integration settings to connect to a real QuickBooks account.`);
        }
        // For real connections, no alert needed - status is shown in UI
      } else {
        setQboConnectionStatus('disconnected');
        const errorMsg = response.data.error || 'Connection test failed';
        alert(`❌ Connection Test Failed\n\n${errorMsg}\n\nPlease check your QuickBooks Online integration settings.`);
        console.warn('QBO Connection failed:', errorMsg);
      }
    } catch (error) {
      setQboConnectionStatus('disconnected');
      const errorMsg = error.response?.data?.error || error.message || 'Connection test failed';
      alert(`❌ Connection Test Failed\n\n${errorMsg}\n\nPlease check your QuickBooks Online integration settings.`);
      console.warn('QBO Connection test failed:', error);
    } finally {
      setQboConnectionLoading(false);
    }
  };

  const handleQBOConnect = async () => {
    if (!selectedQBOIntegration) {
      alert('Please select a QuickBooks Online integration first');
      return;
    }

    try {
      setQboConnectionLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/qbo/auth-url`, {
        params: { integration_vuid: selectedQBOIntegration.vuid }
      });
      
      if (response.data.success) {
        // Open QuickBooks authorization in new window
        const authWindow = window.open(
          response.data.auth_url,
          'qbo-auth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );
        
        // Listen for the callback
        const checkClosed = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(checkClosed);
            // Test connection after window closes
            setTimeout(testQBOConnection, 1000);
          }
        }, 1000);
      } else {
        setError(response.data.error);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to initiate QuickBooks connection');
    } finally {
      setQboConnectionLoading(false);
    }
  };


  const addLineItem = () => {
    const newLineNumber = lineItems.length + 1;
    setLineItems([...lineItems, {
      line_number: newLineNumber,
      gl_account_vuid: '',
      description: '',
      debit_amount: 0,
      credit_amount: 0
    }]);
  };

  // Accounting Export Modal Functions
  const openAccountingExportModal = async () => {
    // Refresh integrations when opening modal
    await fetchJournalEntryIntegrations();
    setShowAccountingExportModal(true);
    setExportStep(1);
    setSelectedAccountingPeriod(filters.accounting_period_vuid || '');
    setSelectedProjects(getExportProjectSelectionFromFilters());
    setSelectedDataTypes({
      journals: false,
      ap_invoices: false,
      project_billings: false
    });
    // Reset selected integration when opening modal
    setSelectedQBOIntegration(null);
    setQboConnectionStatus('disconnected');
    setQboCompanyInfo(null);
    setQboMockMode(false);
  };

  const closeAccountingExportModal = () => {
    setShowAccountingExportModal(false);
    setExportStep(1);
    setSelectedAccountingPeriod('');
    setSelectedProjects([]);
    setSelectedDataTypes({
      journals: false,
      ap_invoices: false,
      project_billings: false
    });
  };

  const handleProjectSelection = (projectVuid) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectVuid)) {
        return prev.filter(vuid => vuid !== projectVuid);
      } else {
        return [...prev, projectVuid];
      }
    });
  };

  const handleDataTypeSelection = (dataType) => {
    setSelectedDataTypes(prev => ({
      ...prev,
      [dataType]: !prev[dataType]
    }));
  };

  const nextStep = () => {
    if (exportStep === 1) {
      if (!selectedAccountingPeriod) {
        setError('Please select an accounting period');
        return;
      }
      setExportStep(2);
    } else if (exportStep === 2) {
      if (selectedProjects.length === 0) {
        setError('Please select at least one project');
        return;
      }
      setExportStep(3);
    } else if (exportStep === 3) {
      const hasSelectedDataType = Object.values(selectedDataTypes).some(selected => selected);
      if (!hasSelectedDataType) {
        setError('Please select at least one data type to export');
        return;
      }
      setExportStep(4);
      executeAccountingExport();
    }
  };

  const prevStep = () => {
    if (exportStep > 1) {
      setExportStep(exportStep - 1);
    }
  };

  const executeAccountingExport = async () => {
    try {
      const accountingPeriodVuid = selectedAccountingPeriod;
      if (!accountingPeriodVuid) {
        setError('Please select an accounting period to export');
        return;
      }

      if (!selectedQBOIntegration) {
        setError('Please select an integration');
        return;
      }

      const results = {
        journal_entries: [],
        invoices: [],
        errors: []
      };

      // Fetch all journal entries for the selected period and projects
      // AP invoices and project billings are exported through their journal entries, not separately
      // So we always fetch journal entries regardless of data type selection
      const journalEntriesToExport = [];
      for (const projectVuid of selectedProjects) {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/journal-entries`, {
            params: {
              accounting_period_vuid: accountingPeriodVuid,
              project_vuid: projectVuid
            }
          });
          const entries = response.data || [];
                              journalEntriesToExport.push(...entries);
        } catch (error) {
          console.error(`Error fetching journal entries for project ${projectVuid}:`, error);
        }
      }
      
      // AP invoices and project billings are now exported through journal entries
      // No need to fetch them separately - they will be included in the journal entries export
      const invoicesToExport = [];

      // First, refresh external IDs for all entries to ensure we have the latest mappings
      const allEntryVuids = journalEntriesToExport.map(e => e.vuid);
      await fetchQBOJournalEntryIds(allEntryVuids, selectedQBOIntegration.vuid, journalEntriesToExport);
      
      // Filter out entries that already have external IDs BEFORE processing
      // This ensures they don't appear in the export at all (not even as "already sent")
      const entriesToExport = journalEntriesToExport.filter(entry => {
        // Check if entry already has an external ID based on its type
        // For AP invoice entries (not retainage), check for bill mapping
        if (entry.transaction_type === 'ap_invoice' && entry.reference_type !== 'Retainage') {
          return !qboBillIds[entry.vuid];
        }
        // For project billing entries (not retainage), check for invoice mapping
        else if (entry.transaction_type === 'project_billing' && entry.reference_type !== 'Retainage') {
          return !qboInvoiceIds[entry.vuid];
        }
        // For all other entries (including retainage, project_expense, labor_cost, etc.), check for journal_entry mapping
        else {
          return !qboJournalEntryIds[entry.vuid];
        }
      });
      
                  
      // Send each journal entry individually
      for (const entry of entriesToExport) {
        try {
          
                    const response = await axios.post(
            `${API_BASE_URL}/api/integrations/${selectedQBOIntegration.vuid}/send-journal-entry`,
            { 
              journal_entry_vuid: entry.vuid,
              force_resend: false
            }
          );

          // Handle 207 Multi-Status responses (partial success with warnings)
          if (response.status === 207 && response.data.warning) {
            console.warn(`Partial success for ${entry.journal_number}:`, response.data.warning);
            // Treat warnings as errors for reporting purposes
            results.errors.push({
              type: 'journal_entry',
              journal_number: entry.journal_number,
              error: response.data.warning || 'Partial failure - some components failed to send'
            });
            // Still add to journal_entries if there's partial success
            if (response.data.success) {
              const result = {
                journal_entry_vuid: entry.vuid,
                journal_number: entry.journal_number,
                status: 'partial_success'
              };
              // Include any successful components
              if (response.data.bill) result.bill_id = response.data.bill.qbo_bill_id;
              if (response.data.qbo_journal_entry_id) result.qbo_journal_entry_id = response.data.qbo_journal_entry_id;
              if (response.data.retainage_journal_entry) result.retainage_id = response.data.retainage_journal_entry.qbo_journal_entry_id;
              results.journal_entries.push(result);
            }
            continue;
          }

          // Handle 404 Not Found (journal entry deleted) - refresh list and skip
          if (response.status === 404 || (response.data.error && response.data.error.toLowerCase().includes('not found'))) {
            console.warn(`  → Journal entry not found (may have been deleted), skipping and refreshing list...`);
            results.errors.push({
              type: 'journal_entry',
              journal_number: entry.journal_number,
              error: 'Journal entry not found (may have been deleted)'
            });
            // Refresh the list to remove deleted entries
            await fetchJournalEntries();
            continue;
          }

          // Handle 409 Conflict (already sent) as a non-error - just mark as already sent
          if (response.status === 409 || (response.data.error && response.data.error.includes('already sent'))) {
            const externalId = response.data.qbo_bill_id || response.data.qbo_invoice_id || response.data.qbo_journal_entry_id || 'unknown';
                        results.journal_entries.push({
              journal_entry_vuid: entry.vuid,
              journal_number: entry.journal_number,
              status: 'already_sent',
              qbo_id: externalId
            });
            if (response.data.procore_direct_cost_error) {
              results.errors.push({
                type: 'procore_direct_cost',
                journal_number: entry.journal_number,
                error: response.data.procore_direct_cost_error
              });
            }
            continue;
          }

          if (response.data.success) {
            const result = {
              journal_entry_vuid: entry.vuid,
              journal_number: entry.journal_number,
              status: 'success'
            };

            if (response.data.procore_direct_cost_error) {
              results.errors.push({
                type: 'procore_direct_cost',
                journal_number: entry.journal_number,
                error: response.data.procore_direct_cost_error
              });
            } else if (response.data.procore_direct_cost) {
              result.procore_direct_cost_id = response.data.procore_direct_cost.procore_direct_cost_id;
            }

            // Handle combined entry (Bill + Retainage)
            // IMPORTANT: Only set bill ID for bill records, not retainage entries
            if (response.data.bill && response.data.bill.qbo_bill_id) {
              result.bill_id = response.data.bill.qbo_bill_id;
              result.bill_status = response.data.bill.already_sent ? 'already_sent' : 'created';
              
              // Only update Bill ID if this is the bill record, not a retainage entry
              // Retainage entries have reference_type === 'Retainage'
              if (entry.reference_type !== 'Retainage') {
                setQboBillIds(prev => ({
                  ...prev,
                  [entry.vuid]: response.data.bill.qbo_bill_id
                }));
              }
            }
        
        // Handle Invoice (for project billings)
        // IMPORTANT: Only set invoice ID for invoice records, not retainage entries
        if (response.data.invoice && response.data.invoice.qbo_invoice_id) {
          result.invoice_id = response.data.invoice.qbo_invoice_id;
          result.invoice_status = response.data.invoice.already_sent ? 'already_sent' : 'created';
          
          // Only update Invoice ID if this is the invoice record, not a retainage entry
          // Retainage entries have reference_type === 'Retainage'
          if (entry.reference_type !== 'Retainage') {
            setQboInvoiceIds(prev => ({
              ...prev,
              [entry.vuid]: response.data.invoice.qbo_invoice_id
            }));
          }
        }
        
        if (response.data.retainage_journal_entry && response.data.retainage_journal_entry.qbo_journal_entry_id) {
          result.retainage_id = response.data.retainage_journal_entry.qbo_journal_entry_id;
          result.retainage_status = response.data.retainage_journal_entry.already_sent ? 'already_sent' : 'created';
          
          // Update Retainage Journal Entry ID in state
          setQboJournalEntryIds(prev => ({
            ...prev,
            [entry.vuid]: response.data.retainage_journal_entry.qbo_journal_entry_id
          }));
        } else if (response.data.qbo_journal_entry_id) {
              result.qbo_journal_entry_id = response.data.qbo_journal_entry_id;
              result.journal_number_qbo = response.data.journal_number;
              
              // Update Journal Entry ID in state
              setQboJournalEntryIds(prev => ({
                ...prev,
                [entry.vuid]: response.data.qbo_journal_entry_id
              }));
            }

            results.journal_entries.push(result);
                      } else {
            const errorMsg = response.data.error || 'Unknown error';
            console.error(`  ✗ Failed: ${errorMsg}`);
            results.errors.push({
              type: 'journal_entry',
              journal_number: entry.journal_number,
              error: errorMsg
            });
          }
        } catch (error) {
          // Handle 404 Not Found (journal entry deleted) - refresh list and skip
          if (error.response?.status === 404 || (error.response?.data?.error && error.response.data.error.toLowerCase().includes('not found'))) {
            console.warn(`  → Journal entry not found (may have been deleted), skipping and refreshing list...`);
            results.errors.push({
              type: 'journal_entry',
              journal_number: entry.journal_number || entry.vuid,
              error: 'Journal entry not found (may have been deleted)'
            });
            // Refresh the list to remove deleted entries
            await fetchJournalEntries();
          }
          // Handle 409 Conflict (already sent) as a non-error
          else if (error.response?.status === 409 || (error.response?.data?.error && error.response.data.error.includes('already sent'))) {
            const externalId = error.response?.data?.qbo_bill_id || error.response?.data?.qbo_invoice_id || error.response?.data?.qbo_journal_entry_id || 'unknown';
                        results.journal_entries.push({
              journal_entry_vuid: entry.vuid,
              journal_number: entry.journal_number,
              status: 'already_sent',
              qbo_id: externalId
            });
          } else {
            const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
            console.error(`  ✗ Exception: ${errorMsg}`);
            console.error('Full error:', error);
            results.errors.push({
              type: 'journal_entry',
              journal_number: entry.journal_number || entry.vuid,
              error: errorMsg
            });
          }
        }
      }
      
      
      // AP invoices and project billings are now exported through journal entries
      // The journal entry export handles them using the new code (using total_amount directly)
      // No separate AP invoice export needed

      // Refresh journal entries and QBO IDs to show badges
      await fetchJournalEntries();
      if (selectedQBOIntegration) {
        await fetchQBOJournalEntryIds(
          entriesToExport.map(e => e.vuid),
          selectedQBOIntegration.vuid,
          journalEntries
        );
      }

      // Build success message with details
      const successCount = results.journal_entries.filter(r => r.status === 'success' || r.status === 'partial_success').length + 
                          results.invoices.filter(r => r.status === 'success').length;
      const alreadySentCount = results.journal_entries.filter(r => r.status === 'already_sent').length;
      const errorCount = results.errors.length;
      
      // Log all results for debugging
      
      let message = `✅ Export completed!\n\n`;
      message += `Successfully sent: ${successCount} items\n`;
      if (alreadySentCount > 0) {
        message += `Already sent (skipped): ${alreadySentCount} items\n`;
      }
      if (errorCount > 0) {
        message += `Errors: ${errorCount} items\n`;
      }

      if (results.journal_entries.length > 0) {
        message += `\nJournal Entries:\n`;
        results.journal_entries.forEach(je => {
          if (je.status === 'success' || je.status === 'partial_success') {
            if (je.bill_id) {
              message += `  - ${je.journal_number}: Bill ID ${je.bill_id}`;
              if (je.retainage_id) message += `, Retainage JE ID ${je.retainage_id}`;
            } else if (je.invoice_id) {
              message += `  - ${je.journal_number}: Invoice ID ${je.invoice_id}`;
              if (je.retainage_id) message += `, Retainage JE ID ${je.retainage_id}`;
            } else if (je.qbo_journal_entry_id) {
              message += `  - ${je.journal_number}: QBO JE ID ${je.qbo_journal_entry_id}`;
            }
            if (je.status === 'partial_success') {
              message += ` (⚠️ Partial success - see errors below)`;
            }
            message += `\n`;
          } else if (je.status === 'already_sent') {
            message += `  - ${je.journal_number}: Already sent (ID: ${je.qbo_id})\n`;
          }
        });
      }

      if (results.invoices.length > 0) {
        message += `\nInvoices:\n`;
        results.invoices.forEach(inv => {
          if (inv.qbo_bill_id) {
            message += `  - ${inv.invoice_number}: Bill ID ${inv.qbo_bill_id}\n`;
          }
        });
      }

      if (results.errors.length > 0) {
        // Deduplicate errors by journal_number/invoice_number and error message
        const seenErrors = new Set();
        const uniqueErrors = results.errors.filter(err => {
          const identifier = err.type === 'journal_entry' ? err.journal_number : err.invoice_number;
          const errorMsg = err.error || 'Unknown error';
          const errorKey = `${identifier}:${errorMsg}`;
          if (seenErrors.has(errorKey)) {
            return false;
          }
          seenErrors.add(errorKey);
          return true;
        });
        
        message += `\n\n❌ ERRORS (${uniqueErrors.length} unique, ${results.errors.length} total):\n`;
        message += `${'='.repeat(50)}\n`;
        uniqueErrors.forEach((err, index) => {
          const identifier = err.type === 'journal_entry' ? err.journal_number : err.invoice_number;
          const errorMsg = err.error || 'Unknown error';
          // Count how many times this error occurred
          const duplicateCount = results.errors.filter(e => {
            const eId = e.type === 'journal_entry' ? e.journal_number : e.invoice_number;
            return eId === identifier && (e.error || 'Unknown error') === errorMsg;
          }).length;
          const countLabel = duplicateCount > 1 ? ` (×${duplicateCount})` : '';
          message += `${index + 1}. ${identifier}: ${errorMsg}${countLabel}\n`;
        });
        message += `${'='.repeat(50)}\n`;
      }

      // Log errors to console for debugging
      if (results.errors.length > 0) {
        console.error('Export errors (all):', results.errors);
        console.error('Export errors (unique count):', results.errors.length);
      }
      
      // Store results and show modal for better error visibility
      setExportResults({
        successCount,
        alreadySentCount,
        errorCount,
        journalEntries: results.journal_entries,
        invoices: results.invoices,
        errors: results.errors,
        summaryMessage: message
      });
      
      closeAccountingExportModal();
      
      // Show modal immediately - errors will be visible there
      setShowExportResultsModal(true);
      
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      setError(`Error exporting to accounting system: ${errorMsg}`);
      alert(`❌ Error: ${errorMsg}`);
      console.error('Error:', error);
    }
  };

  // CSV Export Functions
  const handleCsvExport = async () => {
    try {
      setCsvExportLoading(true);
      
      // Get all journal entries (not just the current page)
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const response = await fetch(`${baseURL}/api/journal-entries?${queryParams.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch journal entries');
      }
      
      const allEntries = await response.json();
      
      // Generate CSV content
      const csvContent = generateJournalEntriesCsv(allEntries);
      
      // Download the CSV file
      downloadCsvFile(csvContent, 'journal_entries.csv');
      
    } catch (error) {
      setError('Error exporting CSV: ' + error.message);
      console.error('Error:', error);
    } finally {
      setCsvExportLoading(false);
    }
  };

  const generateJournalEntriesCsv = (entries) => {
    // CSV headers
    const headers = [
      'Journal Number',
      'Entry Date',
      'Description',
      'Project Number',
      'Project Name',
      'Reference Type',
      'Total Debit',
      'Total Credit',
      'Line Items Count'
    ];
    
    // CSV rows
    const rows = entries.map(entry => [
      entry.journal_number || '',
      entry.entry_date || '',
      entry.description || '',
      entry.project?.project_number || 'N/A',
      entry.project?.project_name || 'N/A',
      getReferenceTypeLabel(entry.reference_type) || '',
      formatCurrency(entry.total_debit || 0),
      formatCurrency(entry.total_credit || 0),
      entry.line_items?.length || 0
    ]);
    
    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    
    return csvContent;
  };

  const downloadCsvFile = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Detailed CSV Export with Line Items
  const handleDetailedCsvExport = async () => {
    try {
      setCsvExportLoading(true);
      
      // Get all journal entries with line items
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const response = await fetch(`${baseURL}/api/journal-entries?${queryParams.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch journal entries');
      }
      
      const allEntries = await response.json();
      
      // Generate detailed CSV content with line items
      const csvContent = generateDetailedJournalEntriesCsv(allEntries);
      
      // Download the CSV file
      downloadCsvFile(csvContent, 'journal_entries_detailed.csv');
      
    } catch (error) {
      setError('Error exporting detailed CSV: ' + error.message);
      console.error('Error:', error);
    } finally {
      setCsvExportLoading(false);
    }
  };

  const handleReverseEntry = async (entry) => {
    if (!window.confirm(
      `Are you sure you want to reverse journal entry "${entry.description}"?\n\n` +
      `This requires an open accounting period. A reversing entry will be created that swaps debits and credits ` +
      `to offset the original, and the original will be marked reversed for audit.`
    )) {
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/posted-records/${entry.vuid}/reverse`, {
        reversed_by: 'User' // Could get from auth context
      });

      if (response.data.message) {
        alert('Journal entry reversed successfully');
        fetchJournalEntries(); // Refresh the list
      } else {
        alert('Error reversing journal entry: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error reversing journal entry:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to reverse journal entry';
      alert('Error: ' + errorMessage);
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(`Are you sure you want to delete journal entry "${entry.description}"? This will permanently delete the journal entry and allow you to re-post the transaction. This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/api/posted-records/delete/${entry.vuid}`);

      if (response.data.message) {
        alert('Journal entry deleted successfully. You can now re-post the transaction.');
        fetchJournalEntries(); // Refresh the list
      } else {
        alert('Error deleting journal entry: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting journal entry:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to delete journal entry';
      alert('Error: ' + errorMessage);
    }
  };

  const handleDeleteAllEntriesForTransaction = async (entry) => {
    const transactionType = entry.transaction_type;
    const transactionVuid = entry.transaction_vuid || entry.reference_vuid;
    
    if (!transactionVuid) {
      alert('Error: Could not find transaction ID for this entry.');
      return;
    }

    // Count how many entries exist for this transaction to show in confirmation
    const relatedEntries = journalEntries.filter(e => 
      e.transaction_type === transactionType && 
      (e.transaction_vuid === transactionVuid || e.reference_vuid === transactionVuid)
    );

    const entryCount = relatedEntries.length;
    const entryText = entryCount === 1 ? '1 journal entry' : `${entryCount} journal entries`;

    if (!window.confirm(
      `Are you sure you want to delete ALL ${entryText} for this transaction?\n\n` +
      `This will permanently delete:\n` +
      relatedEntries.map(e => `  • ${e.description}${e.journal_suffix ? ` (${e.journal_suffix})` : ''}`).join('\n') +
      `\n\nThis action cannot be undone and will allow you to re-post the transaction.`
    )) {
      return;
    }

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/posted-records/delete-by-transaction/${transactionType}/${transactionVuid}`
      );

      if (response.data.message) {
        alert(`Successfully deleted ${response.data.deleted_count} journal entry(ies). You can now re-post the transaction.`);
        fetchJournalEntries(); // Refresh the list
      } else {
        alert('Error deleting journal entries: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting all journal entries for transaction:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to delete journal entries';
      alert('Error: ' + errorMessage);
    }
  };


  const generateDetailedJournalEntriesCsv = (entries) => {
    // CSV headers for detailed export
    const headers = [
      'Journal Number',
      'Entry Date',
      'Description',
      'Project Number',
      'Project Name',
      'Reference Type',
      'Line Number',
      'Account Number',
      'Account Name',
      'Line Description',
      'Debit Amount',
      'Credit Amount'
    ];
    
    // CSV rows - one row per line item
    const rows = [];
    entries.forEach(entry => {
      if (entry.line_items && entry.line_items.length > 0) {
        entry.line_items.forEach(line => {
          rows.push([
            entry.journal_number || '',
            entry.entry_date || '',
            entry.description || '',
            entry.project?.project_number || 'N/A',
            entry.project?.project_name || 'N/A',
            getReferenceTypeLabel(entry.reference_type) || '',
            line.line_number || '',
            line.gl_account?.account_number || '',
            line.gl_account?.account_name || '',
            line.description || '',
            formatCurrency(line.debit_amount || 0),
            formatCurrency(line.credit_amount || 0)
          ]);
        });
      } else {
        // If no line items, still include the journal entry
        rows.push([
          entry.journal_number || '',
          entry.entry_date || '',
          entry.description || '',
            entry.project?.project_number || 'N/A',
            entry.project?.project_name || 'N/A',
            getReferenceTypeLabel(entry.reference_type) || '',
            '',
            '',
            '',
            '',
            '',
            ''
        ]);
      }
    });
    
    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    
    return csvContent;
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      const newLines = lineItems.filter((_, i) => i !== index);
      // Renumber lines
      newLines.forEach((line, i) => {
        line.line_number = i + 1;
      });
      setLineItems(newLines);
    }
  };

  const updateLineItem = (index, field, value) => {
    const newLines = [...lineItems];
    newLines[index][field] = value;
    
    // Ensure only one of debit or credit has a value
    if (field === 'debit_amount' && parseFloat(value) > 0) {
      newLines[index].credit_amount = 0;
    } else if (field === 'credit_amount' && parseFloat(value) > 0) {
      newLines[index].debit_amount = 0;
    }
    
    setLineItems(newLines);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };


  const getReferenceTypeLabel = (type) => {
    switch (type) {
      case 'ap_invoice': return 'AP Invoice';
      case 'project_billing': return 'Project Billing';
      case 'labor_cost': return 'Labor Cost';
      case 'project_expense': return 'Project Expense';
      case 'over_billing': return 'Over Billing';
      case 'under_billing': return 'Under Billing';
      case 'over_under_billing': return 'Over/Under Billing';
      case 'gl_insurance': return 'GL Insurance';
      case 'reversal': return 'Reversal';
      case 'ap_invoice_payment': return 'AP Invoice Payment';
      case 'Retainage': return 'Retainage';
      case 'ap_invoice_retainage': return 'AP Invoice - Retainage';
      case 'project_billing_retainage': return 'Project Billing - Retainage';
      default: return type;
    }
  };

  const handlePreviewInvoice = async (entry) => {
    // Only show preview modal for AP invoice and AP retainage entries
    if (entry.reference_type !== 'ap_invoice' && entry.reference_type !== 'ap_invoice_retainage') {
      return;
    }

    setAuditLoading(true);
    setShowAuditModal(true);
    
    try {
      // Use reference_vuid if available, fallback to transaction_vuid for backward compatibility
      const invoiceVuid = entry.reference_vuid || entry.transaction_vuid;
      
      if (!invoiceVuid) {
        setError('No reference ID found for this journal entry');
        setShowAuditModal(false);
        setAuditLoading(false);
        return;
      }
      
      // Fetch the AP invoice details using axios to include JWT token
      const response = await axios.get(`${API_BASE_URL}/api/ap-invoices/${invoiceVuid}`);
      if (response.data) {
        const invoiceData = response.data;
        setAuditData({
          journalEntry: entry,
          apInvoice: invoiceData
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
      setError(`Failed to fetch AP invoice details: ${errorMsg}`);
      setShowAuditModal(false);
    } finally {
      setAuditLoading(false);
    }
  };

  const handlePreviewProjectBilling = async (entry) => {
    // Only show preview modal for project billing and project billing retainage entries
    if (entry.reference_type !== 'project_billing' && entry.reference_type !== 'project_billing_retainage') {
      return;
    }

    setAuditLoading(true);
    setShowAuditModal(true);
    
    try {
      // Use reference_vuid if available, fallback to transaction_vuid for backward compatibility
      const billingVuid = entry.reference_vuid || entry.transaction_vuid;
      
      if (!billingVuid) {
        setError('No reference ID found for this journal entry');
        setShowAuditModal(false);
        setAuditLoading(false);
        return;
      }
      
      // Fetch the project billing details using axios to include JWT token
      const response = await axios.get(`${API_BASE_URL}/api/project-billings/${billingVuid}`);
      if (response.data) {
        const billingData = response.data;
                                setAuditData({
          journalEntry: entry,
          projectBilling: billingData
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
      setError(`Failed to fetch project billing details for preview: ${errorMsg}`);
      setShowAuditModal(false);
    } finally {
      setAuditLoading(false);
    }
  };

  const handlePreviewLaborCost = async (entry) => {
    // Only show preview modal for labor cost entries
    if (entry.reference_type !== 'labor_cost') {
      return;
    }

    setAuditLoading(true);
    setShowAuditModal(true);
    
    try {
      // Use reference_vuid if available, fallback to transaction_vuid for backward compatibility
      const laborCostVuid = entry.reference_vuid || entry.transaction_vuid;
      
      if (!laborCostVuid) {
        setError('No reference ID found for this journal entry');
        setShowAuditModal(false);
        setAuditLoading(false);
        return;
      }
      
      // Fetch the labor cost details using axios to include JWT token
      const response = await axios.get(`${API_BASE_URL}/api/labor-costs/${laborCostVuid}`);
      if (response.data) {
        const laborCostData = response.data;
        setAuditData({
          journalEntry: entry,
          laborCost: laborCostData
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
      setError(`Failed to fetch labor cost details: ${errorMsg}`);
      setShowAuditModal(false);
    } finally {
      setAuditLoading(false);
    }
  };

  const handlePreviewProjectExpense = async (entry) => {
    // Only show preview modal for project expense entries
    if (entry.reference_type !== 'project_expense') {
      return;
    }

    setAuditLoading(true);
    setShowAuditModal(true);
    
    try {
      // Use reference_vuid if available, fallback to transaction_vuid for backward compatibility
      const expenseVuid = entry.reference_vuid || entry.transaction_vuid;
      
      if (!expenseVuid) {
        setError('No reference ID found for this journal entry');
        setShowAuditModal(false);
        setAuditLoading(false);
        return;
      }
      
      // Fetch the project expense details using axios to include JWT token
      const response = await axios.get(`${API_BASE_URL}/api/project-expenses/${expenseVuid}`);
      if (response.data) {
        const expenseData = response.data;
        setAuditData({
          journalEntry: entry,
          projectExpense: expenseData
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
      setError(`Failed to fetch project expense details: ${errorMsg}`);
      setShowAuditModal(false);
    } finally {
      setAuditLoading(false);
    }
  };

  const paginate = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900">Journal Entries</h1>
          <p className="mt-2 text-gray-600">Manage accounting journal entries and transactions</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
              <select
                value={filters.project_vuid}
                onChange={(e) => setFilters({...filters, project_vuid: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Projects</option>
                {projects.map(project => (
                  <option key={project.vuid} value={project.vuid}>
                    {project.project_number} - {project.project_name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Accounting Period <span className="text-red-500">*</span></label>
              <select
                value={filters.accounting_period_vuid}
                onChange={(e) => setFilters({...filters, accounting_period_vuid: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Accounting Period</option>
                {accountingPeriods.map(period => (
                  <option key={period.vuid} value={period.vuid}>
                    {period.month}/{period.year} {period.status === 'open' ? '(Open)' : '(Closed)'}
                  </option>
                ))}
              </select>
            </div>
            
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reference Type</label>
              <select
                value={filters.reference_type}
                onChange={(e) => setFilters({...filters, reference_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="ap_invoice">AP Invoice</option>
                <option value="project_billing">Project Billing</option>
                <option value="labor_cost">Labor Cost</option>
                <option value="project_expense">Project Expense</option>
                <option value="over_billing">Over Billing</option>
                <option value="under_billing">Under Billing</option>
                <option value="over_under_billing">Over/Under Billing</option>
                <option value="gl_insurance">GL Insurance</option>
                <option value="reversal">Reversal</option>
                <option value="ap_invoice_payment">AP Invoice Payment</option>
                <option value="Retainage">Retainage</option>
                <option value="ap_invoice_retainage">AP Invoice - Retainage</option>
                <option value="project_billing_retainage">Project Billing - Retainage</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sync Status</label>
              <select
                value={syncStatus}
                onChange={(e) => setSyncStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="synced">Synced</option>
                <option value="unsynced">Unsynced</option>
              </select>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mb-6 flex flex-wrap justify-center gap-4">
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-claret"
            data-tooltip="Add Journal Entry"
          >
            <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Add Journal Entry</span>
          </button>
          
          <button
            onClick={openAccountingExportModal}
            className="btn-claret"
            data-tooltip="Send to Accounting"
          >
            <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            <span>Send to Accounting</span>
          </button>

          <button
            onClick={handleCsvExport}
            disabled={csvExportLoading}
            className={`btn-claret ${
              csvExportLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            data-tooltip="Export CSV"
          >
            {csvExportLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 + 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Export CSV</span>
              </>
            )}
          </button>

          <button
            onClick={handleDetailedCsvExport}
            disabled={csvExportLoading}
            className={`btn-claret ${
              csvExportLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            data-tooltip="Export Detailed CSV"
          >
            {csvExportLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 + 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Export Detailed CSV</span>
              </>
            )}
          </button>

        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
            <button
              onClick={() => setError(null)}
              className="float-right font-bold"
            >
              ×
            </button>
          </div>
        )}

        {/* Journal Entries Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {!filters.accounting_period_vuid ? (
            <div className="p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No Accounting Period Selected</h3>
              <p className="mt-1 text-sm text-gray-500">Please select an accounting period above to view journal entries.</p>
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('journal_number')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Journal #</span>
                      {getSortIcon('journal_number')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('entry_date')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Date</span>
                      {getSortIcon('entry_date')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('description')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Description</span>
                      {getSortIcon('description')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('project')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Project</span>
                      {getSortIcon('project')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('reference_type')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Reference</span>
                      {getSortIcon('reference_type')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('amount')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Amount</span>
                      {getSortIcon('amount')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getSortedData()
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((entry) => (
                  <tr key={entry.vuid} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {entry.journal_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(entry.entry_date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {entry.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.project ? (
                        <div>
                          <div className="font-medium">{entry.project.project_name || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{entry.project.project_number || ''}</div>
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getReferenceTypeLabel(entry.reference_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(() => {
                        // Calculate from line items if available
                        const lineItemsTotal = entry.line_items?.reduce((sum, line) => sum + parseFloat(line.debit_amount || 0), 0) || 0;
                        // Fallback to total_amount if line items sum is 0 but total_amount exists
                        const displayAmount = lineItemsTotal > 0 ? lineItemsTotal : (entry.total_amount || 0);
                        return formatCurrency(displayAmount);
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        {/* Show synced status if entry has any QBO external ID or is_synced flag */}
                        {(entry.is_synced || qboBillIds[entry.vuid] || qboInvoiceIds[entry.vuid] || qboJournalEntryIds[entry.vuid]) && (
                          <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 gap-1 font-medium">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            {entry.synced_integrations && entry.synced_integrations.length > 0
                              ? `Synced: ${entry.synced_integrations.map((integration) => integration.integration_name || integration.integration_type).join(', ')}`
                              : 'Synced'}
                          </span>
                        )}
                        {entry.exported_to_accounting && !entry.is_synced && !qboBillIds[entry.vuid] && !qboInvoiceIds[entry.vuid] && !qboJournalEntryIds[entry.vuid] && (
                          <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 gap-1 font-medium">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Exported & Locked
                          </span>
                        )}
                        {/* Show Bill ID badge if available */}
                        {qboBillIds[entry.vuid] && (
                          <span 
                            className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 gap-1 font-medium cursor-pointer hover:bg-green-200" 
                            data-tooltip={`QBO Bill ID: ${qboBillIds[entry.vuid]} (click to copy)`}
                            onClick={() => {
                              navigator.clipboard.writeText(qboBillIds[entry.vuid]);
                            }}
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                            </svg>
                            Bill: {qboBillIds[entry.vuid]}
                          </span>
                        )}
                        {/* Show Invoice ID badge if available */}
                        {qboInvoiceIds[entry.vuid] && (
                          <span 
                            className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 gap-1 font-medium cursor-pointer hover:bg-green-200" 
                            data-tooltip={`QBO Invoice ID: ${qboInvoiceIds[entry.vuid]} (click to copy)`}
                            onClick={() => {
                              navigator.clipboard.writeText(qboInvoiceIds[entry.vuid]);
                            }}
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                            </svg>
                            Invoice: {qboInvoiceIds[entry.vuid]}
                          </span>
                        )}
                        {/* Show Journal Entry ID badge if available */}
                        {qboJournalEntryIds[entry.vuid] && (() => {
                          // Determine if this is a retainage entry (from AP invoice or project billing)
                          // A retainage entry will have BOTH a journal entry ID AND a Bill/Invoice ID
                          // Regular journal entries (like labor costs) only have the journal entry ID
                          const isRetainageEntry = 
                            (entry.transaction_type === 'ap_invoice' && qboBillIds[entry.vuid]) ||
                            (entry.transaction_type === 'project_billing' && qboInvoiceIds[entry.vuid]);
                          
                          const label = isRetainageEntry ? 'Retainage' : 'Journal Entry';
                          
                          return (
                            <span 
                              className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 gap-1 font-medium cursor-pointer hover:bg-blue-200" 
                              data-tooltip={`QBO ${label} ID: ${qboJournalEntryIds[entry.vuid]} (click to copy)`}
                              onClick={() => {
                                navigator.clipboard.writeText(qboJournalEntryIds[entry.vuid]);
                              }}
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                              </svg>
                              {label}: {qboJournalEntryIds[entry.vuid]}
                            </span>
                          );
                        })()}
                        <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedEntry(entry);
                            setShowLinesModal(true);
                          }}
                          className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                          data-tooltip="View Lines"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                        
                        {(entry.reference_type === 'ap_invoice' || entry.reference_type === 'ap_invoice_retainage') && (
                          <button
                            onClick={() => handlePreviewInvoice(entry)}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip="Preview source AP invoice"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </button>
                        )}
                        
                        {(entry.reference_type === 'project_billing' || entry.reference_type === 'project_billing_retainage') && (
                          <button
                            onClick={() => handlePreviewProjectBilling(entry)}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip="Preview source project billing"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </button>
                        )}
                        
                        {entry.reference_type === 'labor_cost' && (
                          <button
                            onClick={() => handlePreviewLaborCost(entry)}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip="Preview source labor cost"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </button>
                        )}
                        
                        {entry.reference_type === 'project_expense' && (
                          <button
                            onClick={() => handlePreviewProjectExpense(entry)}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip="Preview source project expense"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </button>
                        )}
                        
                        <button
                          onClick={() => {
                            setSelectedEntry(entry);
                            setShowEditModal(true);
                          }}
                          disabled={entry.exported_to_accounting}
                          className={`text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors ${
                            entry.exported_to_accounting 
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                          data-tooltip={entry.exported_to_accounting ? 'Cannot edit exported journal entry' : 'Edit journal entry'}
                          >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        
                        {/* Send to Integration button */}
                        <button
                          onClick={async () => {
                            setSelectedEntry(entry);
                            setSelectedIntegrationForEntry(null);
                            // Fetch integrations that support journal entries
                            try {
                              const response = await axios.get(`${API_BASE_URL}/api/integrations`);
                              const integrations = response.data.filter(integration => {
                                if (integration.status !== 'active') return false;
                                
                                // QuickBooks Online integrations support journal entries by default
                                if (isErpIntegration(integration)) {
                                  return true;
                                }
                                
                                // For other integrations, check enabled_objects
                                const enabledObjects = integration.enabled_objects || {};
                                return enabledObjects.journal_entries === true || 
                                       enabledObjects.journals === true;
                              });
                              setAllIntegrations(integrations);
                              setShowIntegrationModal(true);
                            } catch (error) {
                              console.error('Error fetching integrations:', error);
                              alert('Error fetching integrations');
                            }
                          }}
                          disabled={sendingToQBO[entry.vuid]}
                          className={`text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors ${
                            sendingToQBO[entry.vuid] 
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                          data-tooltip="Send to integration"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                        </button>
                        
                        {/* Show Mapping button if entry has been sent */}
                        {(qboJournalEntryIds[entry.vuid] || qboBillIds[entry.vuid] || qboInvoiceIds[entry.vuid]) && (
                          <button
                            onClick={() => {
                              handleShowMapping(entry);
                            }}
                            disabled={sendingToQBO[entry.vuid]}
                            className={`text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors ${
                              sendingToQBO[entry.vuid] 
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            }`}
                            data-tooltip="View Integration Mappings"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                        )}
                        
                        {/* Reverse: open period only; creates reversing PostedRecord (debits/credits swapped). Not for reversal-type rows. */}
                        {!entry.reversed_at && entry.transaction_type !== 'reversal' && entry.accounting_period_status === 'open' && (
                          <button
                            onClick={() => handleReverseEntry(entry)}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip="Reverse this journal entry (accounting period must be open)"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        )}
                        
                        {/* Delete button - only show if NOT synced to QBO and not reversed */}
                        {!entry.reversed_at && !(qboJournalEntryIds[entry.vuid] || qboBillIds[entry.vuid] || qboInvoiceIds[entry.vuid]) && (
                          <>
                            <button
                              onClick={() => handleDeleteEntry(entry)}
                              className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                              data-tooltip="Delete this journal entry (only available for unsynced entries, allows re-posting)"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                            {/* Delete All Entries for Transaction button - show for AP invoices and Project Billings (which can have split entries) */}
                            {(entry.transaction_type === 'ap_invoice' || entry.transaction_type === 'project_billing') && (
                              <button
                                onClick={() => handleDeleteAllEntriesForTransaction(entry)}
                                className="text-orange-600 hover:text-orange-800 p-1 hover:bg-orange-50 rounded transition-colors"
                                data-tooltip="Delete ALL journal entries for this transaction (Bill + Retainage + Retention Release if applicable)"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                <span className="sr-only">Delete All</span>
                              </button>
                            )}
                          </>
                        )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {/* Pagination */}
        {filters.accounting_period_vuid && (() => {
          const sortedData = getSortedData();
          const calculatedTotalPages = Math.ceil(sortedData.length / itemsPerPage);
          return calculatedTotalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length} results
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: calculatedTotalPages }, (_, i) => i + 1)
                  .filter(page => page === 1 || page === totalPages || 
                                   (page >= currentPage - 2 && page <= currentPage + 2))
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-3 py-2 text-gray-400">...</span>
                      )}
                      <button
                        onClick={() => paginate(page)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium ${
                          currentPage === page
                            ? 'bg-black text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
                
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === calculatedTotalPages}
                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                      currentPage === calculatedTotalPages
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create Journal Entry</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Accounting Period</label>
                    <select
                      value={formData.accounting_period_vuid}
                      onChange={(e) => setFormData({...formData, accounting_period_vuid: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Period</option>
                      {accountingPeriods.map(period => (
                        <option key={period.vuid} value={period.vuid}>
                          {period.month}/{period.year}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
                    <select
                      value={formData.project_vuid}
                      onChange={(e) => setFormData({...formData, project_vuid: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No Project</option>
                      {projects.map(project => (
                        <option key={project.vuid} value={project.vuid}>
                          {project.project_number} - {project.project_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Entry Date</label>
                    <input
                      type="date"
                      value={formData.entry_date}
                      onChange={(e) => setFormData({...formData, entry_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reference Type</label>
                    <select
                      value={formData.reference_type}
                      onChange={(e) => setFormData({...formData, reference_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Type</option>
                      <option value="ap_invoice">AP Invoice</option>
                      <option value="project_billing">Project Billing</option>
                      <option value="over_billing">Over Billing</option>
                      <option value="under_billing">Under Billing</option>
                    </select>
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter description"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reference VUID</label>
                  <input
                    type="text"
                    value={formData.reference_vuid}
                    onChange={(e) => setFormData({...formData, reference_vuid: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter reference VUID"
                  />
                </div>
                
                {/* Line Items */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">Line Items</label>
                    <button
                      onClick={addLineItem}
                      className="btn-claret"
                      >
                      Add Line
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {lineItems.map((line, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-1">
                          <span className="text-sm text-gray-500">{line.line_number}</span>
                        </div>
                        <div className="col-span-3">
                          <select
                            value={line.gl_account_vuid}
                            onChange={(e) => updateLineItem(index, 'gl_account_vuid', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            required
                          >
                            <option value="">Select Account</option>
                            {glAccounts.map(account => (
                              <option key={account.vuid} value={account.vuid}>
                                {account.account_number} - {account.account_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-3">
                          <input
                            type="text"
                            value={line.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Description"
                            required
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            step="0.01"
                            value={line.debit_amount}
                            onChange={(e) => updateLineItem(index, 'debit_amount', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Debit"
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            step="0.01"
                            value={line.credit_amount}
                            onChange={(e) => updateLineItem(index, 'credit_amount', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Credit"
                          />
                        </div>
                        <div className="col-span-1">
                          {lineItems.length > 1 && (
                            <button
                              onClick={() => removeLineItem(index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateEntry}
                    className="btn-claret"
                    >
                    Create Entry
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Lines Modal */}
        {showLinesModal && selectedEntry && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Journal Entry Lines - {selectedEntry.journal_number}
                </h3>
                
                <div className="mb-4">
                  <p><strong>Description:</strong> {selectedEntry.description}</p>
                  <p><strong>Date:</strong> {formatDate(selectedEntry.entry_date)}</p>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Line</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedEntry.line_items?.map((line) => (
                        <tr key={line.vuid || line.line_number}>
                          <td className="px-4 py-2 text-sm text-gray-900">{line.line_number || '-'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {line.account_name || line.gl_account?.account_name || line.gl_account_vuid || '-'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">{line.description || '-'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">
                            {formatCurrency(line.debit_amount || 0)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">
                            {formatCurrency(line.credit_amount || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-4 text-right">
                  <button
                    onClick={() => setShowLinesModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Accounting Export Modal */}
        {showAccountingExportModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Send to Accounting System
                </h3>
                
                {/* Integration Selection */}
                <div className="mb-6">
                  <label className="block text-lg font-semibold text-gray-900 mb-3">
                    Select Integration *
                  </label>
                  {qboIntegrations.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                      <p className="text-lg mb-2">No integrations found</p>
                      <p className="text-sm">Please create an integration in the Integrations page first.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {qboIntegrations.map((integration) => (
                        <div
                          key={integration.vuid}
                          onClick={async () => {
                            setSelectedQBOIntegration(integration);
                            // Test connection when selecting integration
                            try {
                              setQboConnectionLoading(true);
                              const response = await axios.get(`${API_BASE_URL}/api/qbo/test-connection`, {
                                params: { integration_vuid: integration.vuid }
                              });
                              
                              if (response.data.success) {
                                setQboConnectionStatus('connected');
                                setQboCompanyInfo(response.data.company_name);
                                setQboMockMode(response.data.mock_mode || false);
                                                              } else {
                                setQboConnectionStatus('disconnected');
                                setQboCompanyInfo(null);
                                setQboMockMode(false);
                                console.warn('QBO connection test failed:', response.data.error);
                              }
                            } catch (error) {
                              setQboConnectionStatus('disconnected');
                              setQboCompanyInfo(null);
                              setQboMockMode(false);
                              console.error('QBO connection test error:', error.response?.data || error.message);
                            } finally {
                              setQboConnectionLoading(false);
                            }
                          }}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedQBOIntegration?.vuid === integration.vuid
                              ? 'border-gray-800 bg-gray-50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-900">{integration.integration_name}</h4>
                              {integration.integration_type && (
                                <p className="text-sm text-gray-600 capitalize">
                                  {integration.integration_type.replace('_', ' ')}
                                </p>
                              )}
                            </div>
                            {selectedQBOIntegration?.vuid === integration.vuid && (
                              <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Connection Status - Only show for integrations that require connection testing (e.g., QBO) */}
                {selectedQBOIntegration && selectedQBOIntegration.integration_type === 'quickbooks_online' && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">
                          {qboConnectionStatus === 'connected' ? '✅' : '❌'}
                        </span>
                        <div>
                          <h4 className="text-md font-medium text-gray-900">
                            {selectedQBOIntegration.integration_name} Connection
                          </h4>
                          <p className={`text-sm ${
                            qboConnectionStatus === 'connected' 
                              ? 'text-green-600' 
                              : 'text-red-600'
                          }`}>
                            {qboConnectionStatus === 'connected' 
                              ? `Connected to ${qboCompanyInfo || selectedQBOIntegration.integration_name}`
                              : `Not connected to ${selectedQBOIntegration.integration_name}`
                            }
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {qboConnectionStatus === 'connected' ? (
                          <button
                            onClick={testQBOConnection}
                            disabled={qboConnectionLoading}
                            className="btn-claret"
                            >
                            {qboConnectionLoading ? 'Testing...' : 'Test Connection'}
                          </button>
                        ) : (
                          <button
                            onClick={handleQBOConnect}
                            disabled={qboConnectionLoading}
                            className="btn-claret"
                            >
                            {qboConnectionLoading ? 'Connecting...' : `Connect to ${selectedQBOIntegration.integration_name}`}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 1: Select Accounting Period */}
                {exportStep === 1 && (
                  <div>
                    <div className="mb-4">
                      <h4 className="text-md font-medium text-gray-700 mb-2">Step 1: Select Accounting Period</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Choose the accounting period for the data you want to export.
                      </p>
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="accounting-period" className="block text-sm font-medium text-gray-700 mb-2">
                        Accounting Period
                      </label>
                      <select
                        id="accounting-period"
                        value={selectedAccountingPeriod}
                        onChange={(e) => setSelectedAccountingPeriod(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">Select Accounting Period</option>
                        {accountingPeriods.map(period => (
                          <option key={period.vuid} value={period.vuid}>
                            {period.month}/{period.year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Step 2: Select Projects */}
                {exportStep === 2 && (
                  <div>
                    <div className="mb-4">
                      <h4 className="text-md font-medium text-gray-700 mb-2">Step 2: Select Projects</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Choose which projects to export data for. You can select multiple projects.
                      </p>
                      <div className="flex justify-between items-center mb-4">
                        <button
                          type="button"
                          onClick={() => {
                            const allProjectVuids = projects.map(p => p.vuid);
                            setSelectedProjects(allProjectVuids);
                          }}
                          className="btn-claret"
                          >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedProjects([])}
                          className="btn-claret"
                          >
                          Clear All
                        </button>
                      </div>
                    </div>
                    
                    <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                      <div className="space-y-2 p-4">
                        {projects.map(project => (
                          <label key={project.vuid} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={selectedProjects.includes(project.vuid)}
                              onChange={() => handleProjectSelection(project.vuid)}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {project.project_number} - {project.project_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {project.client_name || 'No Client'}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {selectedProjects.length > 0 && (
                      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800">
                          <strong>{selectedProjects.length}</strong> project{selectedProjects.length > 1 ? 's' : ''} selected
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3: Select Data Types */}
                {exportStep === 3 && (
                  <div>
                    <div className="mb-4">
                      <h4 className="text-md font-medium text-gray-700 mb-2">Step 3: Select Data Types</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Choose which types of data to export to the accounting system.
                      </p>
                    </div>
                    
                    <div className="space-y-4">
                      <label className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-4 border border-gray-200 rounded-lg">
                        <input
                          type="checkbox"
                          checked={selectedDataTypes.journals}
                          onChange={() => handleDataTypeSelection('journals')}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            💰 Journal Entries
                          </div>
                          <div className="text-xs text-gray-500">
                            Export accounting journal entries and transactions
                          </div>
                        </div>
                      </label>

                      <label className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-4 border border-gray-200 rounded-lg">
                        <input
                          type="checkbox"
                          checked={selectedDataTypes.ap_invoices}
                          onChange={() => handleDataTypeSelection('ap_invoices')}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            AP Invoices
                          </div>
                          <div className="text-xs text-gray-500">
                            Export accounts payable invoices (vendor bills)
                          </div>
                        </div>
                      </label>

                      <label className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-4 border border-gray-200 rounded-lg">
                        <input
                          type="checkbox"
                          checked={selectedDataTypes.project_billings}
                          onChange={() => handleDataTypeSelection('project_billings')}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            🏗️ Project Billings
                          </div>
                          <div className="text-xs text-gray-500">
                            Export accounts receivable invoices (customer billings)
                          </div>
                        </div>
                      </label>
                      
                    </div>
                  </div>
                )}

                {/* Step 4: Export Progress */}
                {exportStep === 4 && (
                  <div className="text-center">
                    <div className="mb-4">
                      <h4 className="text-md font-medium text-gray-700 mb-2">Step 4: Exporting Data</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Please wait while we export your data to the accounting system...
                      </p>
                    </div>
                    
                    <div className="flex justify-center">
                      <svg className="animate-spin h-8 w-8 text-green-600" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 + 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                    </div>
                  </div>
                )}

                {/* Modal Actions */}
                <div className="mt-6 flex justify-between">
                  <div>
                    {exportStep > 1 && (
                      <button
                        onClick={prevStep}
                        className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                      >
                        Previous
                      </button>
                    )}
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={closeAccountingExportModal}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    
                    {exportStep < 4 && (
                      <button
                        onClick={nextStep}
                        className="btn-claret"
                        >
                        {exportStep === 1 ? 'Next' : exportStep === 2 ? 'Next' : 'Export Data'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Audit Modal */}
        {showAuditModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {auditData?.apInvoice ? 'Preview Invoice' : 
                     auditData?.projectBilling ? 'Preview Billing' :
                     auditData?.laborCost ? 'Preview Labor Cost' :
                     auditData?.projectExpense ? 'Preview Expense' : 'Preview'} - {auditData?.journalEntry?.journal_number}
                  </h3>
                  <button
                    onClick={() => setShowAuditModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {auditLoading ? (
                  <div className="flex justify-center py-8">
                    <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 + 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                  </div>
                ) : auditData ? (
                  <div className="space-y-6">
                    {/* Journal Entry Summary */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Related Journal Entry</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-600">Journal Number:</span>
                          <span className="ml-2 text-gray-900">{auditData.journalEntry.journal_number}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Entry Date:</span>
                          <span className="ml-2 text-gray-900">{formatDate(auditData.journalEntry.entry_date)}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Description:</span>
                          <span className="ml-2 text-gray-900">{auditData.journalEntry.description}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Reference Type:</span>
                          <span className="ml-2 text-gray-900">{getReferenceTypeLabel(auditData.journalEntry.reference_type)}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Total Amount:</span>
                          <span className="ml-2 text-gray-900">
                            {formatCurrency(auditData.journalEntry.line_items?.reduce((sum, line) => sum + parseFloat(line.debit_amount || 0), 0) || 0)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Source Document Details */}
                    {auditData.apInvoice ? (
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">AP Invoice Details</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-600">Invoice Number:</span>
                            <span className="ml-2 text-gray-900">{auditData.apInvoice.invoice_number}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Invoice Date:</span>
                            <span className="ml-2 text-gray-900">{formatDate(auditData.apInvoice.invoice_date)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Vendor:</span>
                            <span className="ml-2 text-gray-900">{auditData.apInvoice.vendor?.vendor_name || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Total Amount:</span>
                            <span className="ml-2 text-gray-900">{formatCurrency(auditData.apInvoice.total_amount || 0)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Retainage %:</span>
                            <span className="ml-2 text-gray-900">{(auditData.apInvoice.retainage_percentage || 0).toFixed(1)}%</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Retainage Amount:</span>
                            <span className="ml-2 text-gray-900">{formatCurrency(auditData.apInvoice.retainage_amount || 0)}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="font-medium text-gray-600">Description:</span>
                            <span className="ml-2 text-gray-900">{auditData.apInvoice.description || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    ) : auditData.projectBilling ? (
                      <div className="bg-green-50 rounded-lg p-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">Project Billing Details</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-600">Billing Number:</span>
                            <span className="ml-2 text-gray-900">{auditData.projectBilling.billing_number}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Billing Date:</span>
                            <span className="ml-2 text-gray-900">{formatDate(auditData.projectBilling.billing_date)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Project:</span>
                            <span className="ml-2 text-gray-900">{auditData.projectBilling.project?.project_name || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Contract:</span>
                            <span className="ml-2 text-gray-900">{auditData.projectBilling.contract?.contract_number || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Total Amount:</span>
                            <span className="ml-2 text-gray-900">{formatCurrency(auditData.projectBilling.gross_amount || auditData.projectBilling.total_amount || 0)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Retainage %:</span>
                            <span className="ml-2 text-gray-900">{(auditData.projectBilling.retainage_percentage || 0).toFixed(1)}%</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Retainage Amount:</span>
                            <span className="ml-2 text-gray-900">{formatCurrency(auditData.projectBilling.retention_held || 0)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Status:</span>
                            <span className="ml-2 text-gray-900">{auditData.projectBilling.status || 'N/A'}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="font-medium text-gray-600">Description:</span>
                            <span className="ml-2 text-gray-900">{auditData.projectBilling.description || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    ) : auditData.laborCost ? (
                      <div className="bg-orange-50 rounded-lg p-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">Labor Cost Details</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-600">Employee:</span>
                            <span className="ml-2 text-gray-900">{auditData.laborCost.employee?.employee_name || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Payroll Date:</span>
                            <span className="ml-2 text-gray-900">{formatDate(auditData.laborCost.payroll_date)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Project:</span>
                            <span className="ml-2 text-gray-900">{auditData.laborCost.project?.project_name || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Cost Code:</span>
                            <span className="ml-2 text-gray-900">{auditData.laborCost.cost_code?.cost_code || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Cost Type:</span>
                            <span className="ml-2 text-gray-900">{auditData.laborCost.cost_type?.cost_type || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Hours:</span>
                            <span className="ml-2 text-gray-900">{auditData.laborCost.hours || 0}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Rate:</span>
                            <span className="ml-2 text-gray-900">{formatCurrency(auditData.laborCost.rate || 0)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Amount:</span>
                            <span className="ml-2 text-gray-900">{formatCurrency(auditData.laborCost.amount || 0)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Status:</span>
                            <span className="ml-2 text-gray-900">{auditData.laborCost.status || 'N/A'}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="font-medium text-gray-600">Memo:</span>
                            <span className="ml-2 text-gray-900">{auditData.laborCost.memo || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    ) : auditData.projectExpense ? (
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">Project Expense Details</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-600">Expense Number:</span>
                            <span className="ml-2 text-gray-900">{auditData.projectExpense.expense_number || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Expense Date:</span>
                            <span className="ml-2 text-gray-900">{formatDate(auditData.projectExpense.expense_date)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Project:</span>
                            <span className="ml-2 text-gray-900">{auditData.projectExpense.project?.project_name || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Vendor:</span>
                            <span className="ml-2 text-gray-900">{auditData.projectExpense.vendor?.vendor_name || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Cost Code:</span>
                            <span className="ml-2 text-gray-900">{auditData.projectExpense.cost_code?.cost_code || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Cost Type:</span>
                            <span className="ml-2 text-gray-900">{auditData.projectExpense.cost_type?.cost_type || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Amount:</span>
                            <span className="ml-2 text-gray-900">{formatCurrency(auditData.projectExpense.amount || 0)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Status:</span>
                            <span className="ml-2 text-gray-900">{auditData.projectExpense.status || 'N/A'}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="font-medium text-gray-600">Description:</span>
                            <span className="ml-2 text-gray-900">{auditData.projectExpense.description || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {/* Line Items */}
                    {auditData.apInvoice?.line_items && auditData.apInvoice.line_items.length > 0 && (
                      <div className="bg-white border rounded-lg p-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">Invoice Line Items</h4>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line #</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Code</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Type</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {auditData.apInvoice.line_items.map((line, index) => (
                                <tr key={line.vuid || index}>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{line.line_number || index + 1}</td>
                                  <td className="px-3 py-2 text-sm text-gray-900">{line.description || 'N/A'}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{line.cost_code?.cost_code || 'N/A'}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{line.cost_type?.cost_type || 'N/A'}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(line.amount || 0)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {auditData.projectBilling?.line_items && auditData.projectBilling.line_items.length > 0 && (
                      <div className="bg-white border rounded-lg p-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">Billing Line Items</h4>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line #</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Code</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Type</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Contract Amount</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Billed to Date</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actual Billing</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Retention Held</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {auditData.projectBilling.line_items.map((line, index) => (
                                <tr key={line.vuid || index}>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{line.line_number || index + 1}</td>
                                  <td className="px-3 py-2 text-sm text-gray-900">{line.description || 'N/A'}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{line.cost_code?.code || line.cost_code?.cost_code || 'N/A'}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{line.cost_type?.cost_type || line.cost_type?.abbreviation || 'N/A'}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(line.contract_amount || 0)}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(line.billed_to_date || 0)}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(line.actual_billing_amount || 0)}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(line.retention_held || 0)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No source data available
                  </div>
                )}

                {/* Modal Actions */}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setShowAuditModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Mapping Modal */}
        {showMappingModal && selectedEntry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Integration Mappings</h2>
                <button
                  onClick={() => {
                    setShowMappingModal(false);
                    setEntryMappings([]);
                    setEditingMapping(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Journal Entry: <span className="font-semibold">{selectedEntry.journal_number || selectedEntry.vuid}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Transaction Type: <span className="font-semibold">{selectedEntry.transaction_type || 'N/A'}</span>
                </p>
              </div>
              
              {mappingLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-gray-600">Loading mappings...</p>
                </div>
              ) : entryMappings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No mappings found for this journal entry.
                </div>
              ) : (
                <div className="space-y-4">
                  {entryMappings.map((mapping) => {
                    const isEditing = editingMapping === mapping.vuid;
                    let label = 'Journal Entry';
                    if (mapping.object_type === 'ap_invoice') {
                      label = 'Bill';
                    } else if (mapping.object_type === 'project_billing') {
                      label = 'Invoice';
                    } else if (mapping.object_type === 'journal_entry') {
                      label = mapping.external_metadata?.type === 'retainage' 
                        ? 'Journal Entry (Retainage)'
                        : 'Journal Entry';
                    }
                    
                    // Get QBO URL - only for QBO integrations
                    // Use integration_type from mapping if available, otherwise try integrationMetadata
                    const integrationType = (mapping.integration_type || '').toLowerCase();
                    const isQboIntegration = integrationType.includes('quickbooks') || integrationType.includes('qbo');
                    
                    // Get environment - prefer from integrationMetadata, fall back to 'production'
                    const integrationInfo = integrationMetadata[mapping.integration_vuid];
                    const integrationEnv = integrationInfo?.environment || 'production';
                    
                    // Only construct QBO URL for QBO integrations
                    // First check if URL is already in metadata (from fetchEntryMappings), otherwise construct it
                    const qboUrl = isQboIntegration 
                      ? (mapping.external_metadata?.qbo_url || 
                         (mapping.external_id && mapping.external_object_type
                           ? constructQboUrl(
                               mapping.external_id,
                               mapping.external_object_type,
                               integrationEnv
                             )
                           : null))
                      : null;
                    
                    return (
                      <div key={mapping.vuid} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold text-gray-700">{label}</span>
                              <span className="text-xs text-gray-500">({mapping.integration_name})</span>
                            </div>
                            {isEditing ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={editedMappingId}
                                  onChange={(e) => setEditedMappingId(e.target.value)}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="External ID"
                                />
                                <button
                                  onClick={() => handleSaveMapping(mapping)}
                                  className="btn-claret"
                                  >
                                  Save
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingMapping(null);
                                    setEditedMappingId('');
                                  }}
                                  className="px-3 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                <p className="text-sm text-gray-900">
                                  <span className="font-medium">External ID:</span> {mapping.external_id}
                                </p>
                                {qboUrl && (
                                  <p className="text-sm">
                                    <a
                                      href={qboUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                      </svg>
                                      View in QuickBooks Online
                                    </a>
                                  </p>
                                )}
                                <p className="text-xs text-gray-500">
                                  <span className="font-medium">Object Type:</span> {mapping.object_type}
                                </p>
                                {mapping.external_metadata && Object.keys(mapping.external_metadata).length > 0 && (
                                  <details className="text-xs text-gray-500">
                                    <summary className="cursor-pointer hover:text-gray-700">
                                      <span className="font-medium">Metadata</span>
                                    </summary>
                                    <pre className="mt-1 p-2 bg-gray-50 rounded text-xs overflow-auto">
                                      {JSON.stringify(mapping.external_metadata, null, 2)}
                                    </pre>
                                  </details>
                                )}
                              </div>
                            )}
                          </div>
                          {!isEditing && (
                            <div className="flex gap-2 ml-4">
                              <button
                                onClick={() => handleEditMapping(mapping)}
                                className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-xs font-medium"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleClearMappingFromModal(mapping, selectedEntry)}
                                className="px-3 py-1.5 bg-red-100 text-red-700 rounded-md hover:bg-red-200 text-xs font-medium"
                              >
                                Clear
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setShowMappingModal(false);
                    setEntryMappings([]);
                    setEditingMapping(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Send to Integration Modal */}
        {showIntegrationModal && selectedEntry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Send Journal Entry to Integration</h2>
                <button
                  onClick={() => {
                    setShowIntegrationModal(false);
                    setSelectedEntry(null);
                    setSelectedIntegrationForEntry(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Journal Entry: <span className="font-semibold">{selectedEntry.journal_number}</span>
                </p>
              </div>
              
              <div className="space-y-4">
                <label className="block text-lg font-semibold text-gray-900 mb-3">
                  Select Integration *
                </label>
                {allIntegrations.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                    <p className="text-lg mb-2">No integrations found</p>
                    <p className="text-sm">Please create an integration that supports journal entries in the Integrations page first.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {allIntegrations.map((integration) => (
                      <div
                        key={integration.vuid}
                        onClick={() => setSelectedIntegrationForEntry(integration)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedIntegrationForEntry?.vuid === integration.vuid
                            ? 'border-gray-800 bg-gray-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900">{integration.integration_name}</h4>
                            {integration.integration_type && (
                              <p className="text-sm text-gray-600 capitalize">
                                {integration.integration_type.replace(/_/g, ' ')}
                              </p>
                            )}
                          </div>
                          {selectedIntegrationForEntry?.vuid === integration.vuid && (
                            <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-4 pt-6">
                <button
                  onClick={() => {
                    setShowIntegrationModal(false);
                    setSelectedEntry(null);
                    setSelectedIntegrationForEntry(null);
                  }}
                  className="btn-claret"
                  >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!selectedIntegrationForEntry) {
                      alert('Please select an integration');
                      return;
                    }
                    
                    // Check if already sent
                    const hasMapping = qboJournalEntryIds[selectedEntry.vuid] || qboBillIds[selectedEntry.vuid] || qboInvoiceIds[selectedEntry.vuid];
                    let forceResend = false;
                    let confirmed = false;
                    
                    if (hasMapping) {
                      if (!window.confirm(`This journal entry has already been sent to ${selectedIntegrationForEntry.integration_name}.\n\nWould you like to re-send it? The system will use existing mappings or create new ones as needed.`)) {
                        return;
                      }
                      confirmed = true;
                      // Don't clear mappings automatically - let the backend handle it
                      // The backend will check for existing mappings and only create new ones if needed
                      // Users can explicitly clear mappings using the "Clear Mapping" button if needed
                      forceResend = true;
                    } else {
                      if (!window.confirm(`Send journal entry ${selectedEntry.journal_number} to ${selectedIntegrationForEntry.integration_name}?`)) {
                        return;
                      }
                      confirmed = true;
                    }
                    
                    if (!confirmed) {
                      return;
                    }
                    
                    // Close modal
                    setShowIntegrationModal(false);
                    const entryToSend = selectedEntry;
                    const integrationToUse = selectedIntegrationForEntry;
                    setSelectedEntry(null);
                    setSelectedIntegrationForEntry(null);
                    
                    // Send to integration (forceResend is already set based on confirmation)
                    await handleSendToIntegration(entryToSend, integrationToUse, forceResend);
                  }}
                  disabled={!selectedIntegrationForEntry}
                  className="btn-claret"
                  >
                  Send to Integration
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Export Results Modal */}
        {showExportResultsModal && exportResults && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Export Results
                  </h3>
                  <button
                    onClick={() => setShowExportResultsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Summary */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-green-600">{exportResults.successCount}</div>
                      <div className="text-sm text-gray-600">Successfully Sent</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600">{exportResults.alreadySentCount}</div>
                      <div className="text-sm text-gray-600">Already Sent</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-600">{exportResults.errorCount}</div>
                      <div className="text-sm text-gray-600">Errors</div>
                    </div>
                  </div>
                </div>

                {/* Errors Section - Show prominently if there are errors */}
                {exportResults.errors.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-md font-semibold text-red-600 mb-3">
                      ❌ Errors ({exportResults.errors.length})
                    </h4>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                      {exportResults.errors.map((err, index) => {
                        const identifier = err.type === 'journal_entry' ? err.journal_number : err.invoice_number;
                        const errorMsg = err.error || 'Unknown error';
                        return (
                          <div key={index} className="mb-3 pb-3 border-b border-red-200 last:border-b-0">
                            <div className="font-semibold text-red-800">{identifier}</div>
                            <div className="text-sm text-red-700 mt-1">{errorMsg}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Successfully Sent Journal Entries */}
                {exportResults.journalEntries.filter(je => je.status === 'success' || je.status === 'partial_success').length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-md font-semibold text-green-600 mb-3">
                      ✓ Successfully Sent Journal Entries
                    </h4>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                      {exportResults.journalEntries
                        .filter(je => je.status === 'success' || je.status === 'partial_success')
                        .map((je, index) => (
                          <div key={index} className="mb-2 text-sm">
                            <span className="font-medium">{je.journal_number}</span>
                            {je.bill_id && <span className="ml-2">- Bill ID: {je.bill_id}</span>}
                            {je.invoice_id && <span className="ml-2">- Invoice ID: {je.invoice_id}</span>}
                            {je.retainage_id && <span className="ml-2">- Retainage JE ID: {je.retainage_id}</span>}
                            {je.qbo_journal_entry_id && <span className="ml-2">- QBO JE ID: {je.qbo_journal_entry_id}</span>}
                            {je.status === 'partial_success' && <span className="ml-2 text-orange-600">(⚠️ Partial success)</span>}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Already Sent Journal Entries */}
                {exportResults.journalEntries.filter(je => je.status === 'already_sent').length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-md font-semibold text-blue-600 mb-3">
                      ⏭️ Already Sent (Skipped)
                    </h4>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                      {exportResults.journalEntries
                        .filter(je => je.status === 'already_sent')
                        .map((je, index) => (
                          <div key={index} className="mb-2 text-sm">
                            <span className="font-medium">{je.journal_number}</span>
                            <span className="ml-2 text-gray-600">- Already sent (ID: {je.qbo_id})</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Close Button */}
                <div className="mt-6 text-right">
                  <button
                    onClick={() => setShowExportResultsModal(false)}
                    className="btn-claret"
                    >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JournalEntries;
